// src/pages/AgendarExamen.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useFuncion } from "../context/FuncionContext";
import { useAuth } from "../context/AuthContext";

function formatTime(t) {
  if (!t) return "";
  try {
    return new Date(`2000-01-01T${String(t).slice(0, 5)}`).toLocaleTimeString(
      "es-EC",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return String(t).slice(0, 5);
  }
}

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("es-EC", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return String(d);
  }
}

function formatDateShort(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("es-EC", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

function boolish(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "1", "si", "sí", "yes", "on"].includes(s)) return true;
    if (["false", "0", "no", "off"].includes(s)) return false;
  }
  return false;
}

function requirementEnabled(v) {
  const s = String(v ?? "no_requerido").trim().toLowerCase();
  return s === "requerido" || s === "opcional";
}

function scheduleNeedsCamera(h) {
  return boolish(h?.requiere_camara_microfono) || requirementEnabled(h?.camara_nivel);
}

function scheduleNeedsMic(h) {
  return boolish(h?.requiere_camara_microfono) || requirementEnabled(h?.microfono_nivel);
}

const requiresTerms = (h) =>
  boolish(h?.is_virtual) || scheduleNeedsCamera(h) || scheduleNeedsMic(h);

function msToNice(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}h ${String(mm).padStart(2, "0")}m`;
  if (mm > 0) return `${mm}m ${String(ss).padStart(2, "0")}s`;
  return `${ss}s`;
}

function ExamDocIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-svg-icon">
      <path
        d="M8 3.75h6.4c.4 0 .78.16 1.06.44l3.35 3.35c.28.28.44.66.44 1.06V18.5A1.75 1.75 0 0 1 17.5 20.25h-9A1.75 1.75 0 0 1 6.75 18.5v-13A1.75 1.75 0 0 1 8.5 3.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.5 3.9v3.1c0 .41.34.75.75.75h3.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 11.25h5.5M9.5 14.25h5.5M9.5 17.25h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon">
      <path
        d="M8 3.75v2.5M16 3.75v2.5M4.75 9.25h14.5M6.5 5.75h11a1.75 1.75 0 0 1 1.75 1.75v10a1.75 1.75 0 0 1-1.75 1.75h-11a1.75 1.75 0 0 1-1.75-1.75v-10A1.75 1.75 0 0 1 6.5 5.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon">
      <circle
        cx="12"
        cy="12"
        r="8.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <path
        d="M9.75 9.4a2.4 2.4 0 1 1 4.05 1.72c-.66.62-1.55 1.13-1.55 2.13"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="16.85" r="1" fill="currentColor" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path
        d="M2.75 12s3.4-5.25 9.25-5.25S21.25 12 21.25 12 17.85 17.25 12 17.25 2.75 12 2.75 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="2.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}


function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <circle cx="10.8" cy="10.8" r="6.1" fill="none" stroke="currentColor" strokeWidth="1.9" />
      <path d="M15.4 15.4l4 4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.9" />
      <path d="M12 7.8v4.6l3.1 1.9" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path d="M4.75 8.5h3.1l1.2-2h5.9l1.2 2h3.1v9.25H4.75V8.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <rect x="9" y="4" width="6" height="10" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6.75 11.75a5.25 5.25 0 0 0 10.5 0M12 17v3M9.5 20h5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path d="M9.6 14.4l4.8-4.8M10.7 7.1l.9-.9a4 4 0 0 1 5.7 5.7l-.9.9M13.3 16.9l-.9.9a4 4 0 0 1-5.7-5.7l.9-.9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BarChartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path d="M5 19V5M5 19h14M9 16v-5M13 16V8M17 16v-8" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path
        d="M5.5 5.5h5v5h-5v-5ZM13.5 5.5h5v5h-5v-5ZM5.5 13.5h5v5h-5v-5ZM13.5 13.5h5v5h-5v-5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path
        d="M8 7h11M8 12h11M8 17h11M5 7h.01M5 12h.01M5 17h.01"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SortIcon({ active = false, direction = "asc" }) {
  return (
    <span className={`agx-sort-icon ${active ? "is-active" : ""}`} aria-hidden="true">
      <span className={direction === "asc" ? "is-selected" : ""}>▲</span>
      <span className={direction === "desc" ? "is-selected" : ""}>▼</span>
    </span>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path
        d="M5 12h13M13 7l5 5-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-inline-icon ag-inline-icon-sm">
      <path
        d="M7 7l10 10M17 7L7 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function AgendarExamen({ gateState = null }) {
  const { codigoCurso } = useParams();
  const [searchParams] = useSearchParams();
  const qaBypass = searchParams.get("qa") === "1";
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    resolveStudentCourseByCode,
    fetchCourseExamSchedules,
    selfScheduleExam,
    fetchMyExamBookings,
    fetchExamTermsCurrent,
    getCourseProgress,
    toNum,
    cancelMyExamBooking,
  } = useFuncion();

  const schedulesSectionRef = React.useRef(null);

  const fnsRef = React.useRef({
    resolveStudentCourseByCode,
    fetchCourseExamSchedules,
    selfScheduleExam,
    fetchMyExamBookings,
    fetchExamTermsCurrent,
    getCourseProgress,
    cancelMyExamBooking,
  });

  useEffect(() => {
    fnsRef.current = {
      resolveStudentCourseByCode,
      fetchCourseExamSchedules,
      selfScheduleExam,
      fetchMyExamBookings,
      fetchExamTermsCurrent,
      getCourseProgress,
      cancelMyExamBooking,
    };
  }, [
    resolveStudentCourseByCode,
    fetchCourseExamSchedules,
    selfScheduleExam,
    fetchMyExamBookings,
    fetchExamTermsCurrent,
    getCourseProgress,
    cancelMyExamBooking,
  ]);

  const COOLDOWN_MS = 5 * 1000;

  function toMs(any) {
    if (any == null) return null;
    if (typeof any === "number")
      return any > 1e12 ? any : any > 1e10 ? any : any * 1000;
    if (typeof any === "string") {
      const n = Number(any);
      if (Number.isFinite(n))
        return n > 1e12 ? n : n > 1e10 ? n : n * 1000;
      const t = Date.parse(any);
      return Number.isFinite(t) ? t : null;
    }
    return null;
  }

  function formatBookedAt(value) {
    const ms = toMs(value);
    if (ms == null) return "—";
    try {
      const d = new Date(ms);
      return `${d.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} · ${d.toLocaleTimeString("es-EC", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return "—";
    }
  }

  function getBookedAtMs(b) {
    const candidates = [
      b?.agendado_en_ts,
      b?.fecha_agendamiento,
      b?.fechaAgendamiento,
      b?.agendado_el,
      b?.created_at,
      b?.createdAt,
      b?.fecha_creacion,
      b?.fecha_creado,
      b?.data?.created_at,
      b?.created_ts,
    ];
    for (const c of candidates) {
      const ms = toMs(c);
      if (ms != null) return ms;
    }
    return null;
  }

  const pickBookingId = (obj) => {
    const candidates = [
      obj?.booking_id,
      obj?.id,
      obj?.bookingId,
      obj?.reserva_id,
      obj?.agendamiento_id,
      obj?.data?.booking_id,
      obj?.data?.id,
      obj?.booking?.id,
    ];
    for (const v of candidates) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const pickScheduleId = (obj) => {
    const candidates = [
      obj?.horario_examen_id,
      obj?.horario_id,
      obj?.schedule_id,
      obj?.horarioId,
      obj?.data?.horario_examen_id,
      obj?.data?.horario_id,
      obj?.booking?.horario_examen_id,
      obj?.booking?.horario_id,
      obj?.schedule?.id,
    ];
    for (const v of candidates) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const normalizeBookings = (list = []) =>
    list.map((x) => {
      const real = pickBookingId(x);
      const createdMs = getBookedAtMs(x);
      const schId = pickScheduleId(x);

      return {
        ...x,
        ...(real != null ? { booking_id: real } : {}),
        ...(createdMs != null ? { agendado_en_ts: createdMs } : {}),
        ...(schId != null ? { horario_examen_id: schId } : {}),
      };
    });

  const enrichBookingsWithSchedules = (bookings = [], schedulesList = []) => {
    const byId = new Map();
    const byDateHour = new Map();

    (schedulesList || []).forEach((h) => {
      const hid = Number(h?.id ?? h?.horario_examen_id);
      if (Number.isFinite(hid)) byId.set(hid, h);

      const d = dateOnly(h?.fecha_examen);
      const hi = hhmmOnly(h?.hora_inicio);
      const hf = hhmmOnly(h?.hora_fin);
      if (d && hi && hf) byDateHour.set(`${d}|${hi}|${hf}`, h);
    });

    return (bookings || []).map((b) => {
      const hid = Number(b?.horario_examen_id);
      const d = dateOnly(b?.fecha_examen);
      const hi = hhmmOnly(b?.hora_inicio);
      const hf = hhmmOnly(b?.hora_fin);
      const h = (Number.isFinite(hid) && byId.get(hid)) || (d && hi && hf ? byDateHour.get(`${d}|${hi}|${hf}`) : null);
      if (!h) return b;

      return {
        ...h,
        ...b,
        horario_examen_id: Number.isFinite(hid) ? hid : Number(h?.id),
        fecha_examen: b?.fecha_examen || h?.fecha_examen,
        hora_inicio: b?.hora_inicio || h?.hora_inicio,
        hora_fin: b?.hora_fin || h?.hora_fin,
        is_virtual: b?.is_virtual ?? h?.is_virtual,
        nota_maxima: b?.nota_maxima ?? h?.nota_maxima,
        tiempo_limite_minutos: b?.tiempo_limite_minutos ?? h?.tiempo_limite_minutos,
        numero_preguntas: b?.numero_preguntas ?? h?.numero_preguntas,
        porcentaje_aprobacion: b?.porcentaje_aprobacion ?? h?.porcentaje_aprobacion,
        ubicacion_info: b?.ubicacion_info ?? h?.ubicacion_info,
        requiere_codigo: b?.requiere_codigo ?? h?.requiere_codigo,
        codigo_horario: b?.codigo_horario ?? h?.codigo_horario,
        habilitacion_unitaria_estudiantes: b?.habilitacion_unitaria_estudiantes ?? h?.habilitacion_unitaria_estudiantes,
        camara_nivel: b?.camara_nivel ?? h?.camara_nivel,
        microfono_nivel: b?.microfono_nivel ?? h?.microfono_nivel,
        numero_fotos_requeridas: b?.numero_fotos_requeridas ?? h?.numero_fotos_requeridas,
        umbral_decibeles_microfono: b?.umbral_decibeles_microfono ?? h?.umbral_decibeles_microfono,
        cierre_por_penalizaciones: b?.cierre_por_penalizaciones ?? h?.cierre_por_penalizaciones,
        limite_penalizaciones: b?.limite_penalizaciones ?? h?.limite_penalizaciones,
        permitir_navegacion: b?.permitir_navegacion ?? h?.permitir_navegacion,
        mostrar_resumen_final: b?.mostrar_resumen_final ?? h?.mostrar_resumen_final,
      };
    });
  };

  const ensureNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  function dateOnly(fecha) {
    if (!fecha) return null;
    const m = String(fecha).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : null;
  }

  function hhmmOnly(horaStr) {
    if (!horaStr) return null;
    const [hh, mm] = String(horaStr).split(":");
    if (hh == null || mm == null) return null;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  }

  function toDateTime(fechaISOorTs, horaStr) {
    const d = dateOnly(fechaISOorTs);
    const hm = hhmmOnly(horaStr);
    if (!d || !hm) return null;

    const [Y, M, D] = d.split("-").map((x) => Number(x));
    const [hh, mm] = hm.split(":").map((x) => Number(x));
    if (![Y, M, D, hh, mm].every(Number.isFinite)) return null;

    const dt = new Date(Y, M - 1, D, hh, mm, 0, 0);
    return isNaN(dt.getTime()) ? null : dt;
  }

  function isSchedulePast(h, now = Date.now()) {
    const end = toDateTime(h?.fecha_examen, h?.hora_fin);
    if (!end) return false;
    return end.getTime() + 5 * 60 * 1000 < now;
  }

  function isWindowOpenFor(b, now = Date.now()) {
    const start = toDateTime(b.fecha_examen, b.hora_inicio);
    const end = toDateTime(b.fecha_examen, b.hora_fin);
    if (!start || !end) return false;
    const TOL = 30 * 1000;
    return now >= start.getTime() - TOL && now <= end.getTime() + TOL;
  }

  function msUntilWindowStart(b, now = Date.now()) {
    const start = toDateTime(b.fecha_examen, b.hora_inicio);
    if (!start) return null;
    return start.getTime() - now;
  }

  function canCancelBooking(b) {
    const start = toDateTime(b.fecha_examen, b.hora_inicio);
    if (!start) return true;
    return Date.now() < start.getTime();
  }

  function getStatusRaw(b) {
    const v =
      b?.booking_status ??
      b?.estado ??
      b?.status ??
      b?.bookingState ??
      b?.data?.booking_status ??
      b?.data?.estado;
    return String(v ?? "").trim();
  }

  function statusKey(b) {
    const s = getStatusRaw(b).toLowerCase();

    if (!s) return "agendado";
    if (s.includes("cancel") || s.includes("anul") || s.includes("rechaz"))
      return "cancelado";
    if (s.includes("expir")) return "expirado";
    if (s.includes("aband")) return "abandonado";
    if (s.includes("complet") || s.includes("finaliz") || s.includes("termin"))
      return "finalizado";
    if (s.includes("progres") || s.includes("inici")) return "en_progreso";
    if (s.includes("confirm")) return "confirmado";
    if (s.includes("pend")) return "pendiente";
    if (s.includes("agend") || s.includes("reserv")) return "agendado";

    return s;
  }

  function bookingAcademicState(b) {
    if (typeof b?.aprobado === "boolean") {
      return b.aprobado ? "Aprobado" : "Reprobado";
    }
    const k = statusKey(b);
    if (k === "cancelado" || k === "expirado") return "Cancelado";
    if (k === "finalizado") return "Finalizado";
    return "Agendado";
  }

  function statusUI(b) {
    const k = statusKey(b);

    const map = {
      agendado: { label: "Agendado", pill: "ag-pill ag-pill-blue" },
      pendiente: { label: "Pendiente", pill: "ag-pill ag-pill-amber" },
      confirmado: { label: "Confirmado", pill: "ag-pill ag-pill-green" },
      en_progreso: { label: "En progreso", pill: "ag-pill ag-pill-purple" },
      cancelado: { label: "Cancelado", pill: "ag-pill ag-pill-gray" },
      expirado: { label: "Expirado", pill: "ag-pill ag-pill-gray" },
      abandonado: { label: "Abandonado", pill: "ag-pill ag-pill-amber" },
      finalizado: { label: "Finalizado", pill: "ag-pill ag-pill-green" },
    };

    return map[k] || { label: getStatusRaw(b) || "Desconocido", pill: "ag-pill ag-pill-gray" };
  }

  function isBookingCancelled(b) {
    const k = statusKey(b);
    return k === "cancelado" || k === "expirado";
  }

  function isBookingFinal(b) {
    const k = statusKey(b);
    return k === "finalizado" || k === "abandonado";
  }

  function isBookingActive(b, now = Date.now()) {
    if (isBookingCancelled(b) || isBookingFinal(b)) return false;
    const end = toDateTime(b.fecha_examen, b.hora_fin);
    if (end && end.getTime() + 5 * 60 * 1000 < now) return false;
    return true;
  }

  function scheduleLocationLabel(h) {
    if (boolish(h?.is_virtual)) return h?.ubicacion_info || h?.ubicacion || "Plataforma virtual";

    return (
      h?.ubicacion_info ||
      h?.ubicacion ||
      h?.location ||
      h?.lugar ||
      h?.aula ||
      h?.laboratorio ||
      "Por confirmar"
    );
  }

  function isUnitControlled(h) {
    return boolish(h?.habilitacion_unitaria_estudiantes);
  }

  function isStudentEnabledForExam(b) {
    if (!isUnitControlled(b)) return true;
    return boolish(b?.habilitado);
  }

  function pickFirstDefined(...values) {
    return values.find((v) => v !== undefined && v !== null && v !== "");
  }

  function numberOrNull(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  function normalizeArray(value) {
    if (Array.isArray(value)) return value;

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  }

  function normalizeObject(value) {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch {
        return {};
      }
    }

    return {};
  }

  function formatSecondsNice(seconds) {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return "—";

    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = Math.floor(n % 60);

    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
  }

  function answerToText(value) {
    if (value == null || value === "") return "Sin respuesta";

    if (Array.isArray(value)) {
      if (!value.length) return "Sin respuesta";
      return value.map(answerToText).join(", ");
    }

    if (typeof value === "object") {
      if (value.label) return String(value.label);
      if (value.texto) return String(value.texto);
      if (value.valor) return String(value.valor);
      if (value.respuesta) return answerToText(value.respuesta);

      try {
        return JSON.stringify(value);
      } catch {
        return "Respuesta registrada";
      }
    }

    if (typeof value === "boolean") return value ? "Verdadero" : "Falso";

    return String(value);
  }

  function initialsFromName(name = "") {
    const parts = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!parts.length) return "EX";

    return parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("");
  }

  function formatDurationClock(seconds) {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return "—";

    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = Math.floor(n % 60);

    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  function formatPercentCompact(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
  }

  function formatScoreCompact(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  }

  function normalizeProctorLabel(value) {
    const s = String(value || "pendiente").toLowerCase().trim();

    const map = {
      pendiente: "Pendiente",
      aprobado: "Aprobado",
      no_aprobado: "No aprobado",
      cancelado: "Cancelado",
      sospechoso: "Sospechoso",
    };

    return map[s] || s.replaceAll("_", " ");
  }

  function getResultPayload(b = {}) {
    const nested = normalizeObject(b?.presentacion || b?.resultado || b?.reporte || b?.summary);

    const rawAnswers = normalizeObject(
      b?.presentacion_respuestas ||
      b?.respuestas ||
      nested?.respuestas
    );

    const grading = normalizeObject(
      rawAnswers?._grading ||
      nested?._grading ||
      nested?.summary ||
      b?.summary
    );

    const detail = normalizeArray(
      b?.detalle_respuestas ||
      b?.detalle ||
      grading?.detalle ||
      nested?.detalle
    );

    const pct = numberOrNull(
      pickFirstDefined(
        b?.porcentaje_obtenido,
        nested?.porcentaje_obtenido,
        nested?.porcentaje,
        grading?.porcentaje
      )
    );

    const score = numberOrNull(
      pickFirstDefined(
        b?.puntuacion_obtenida,
        b?.puntos_ganados_raw,
        nested?.puntuacion_obtenida,
        nested?.nota,
        grading?.puntuacion_obtenida,
        grading?.puntosGanados
      )
    );

    const maxScore = numberOrNull(
      pickFirstDefined(
        b?.puntuacion_maxima,
        b?.puntos_total_raw,
        nested?.puntuacion_maxima,
        b?.nota_maxima,
        nested?.nota_maxima,
        grading?.puntuacion_maxima,
        grading?.puntosTotal
      )
    );

    const required = numberOrNull(
      pickFirstDefined(
        b?.porcentaje_aprobacion,
        nested?.porcentaje_aprobacion,
        grading?.porcentaje_aprobacion,
        70
      )
    );

    const approved =
      typeof b?.aprobado === "boolean"
        ? b.aprobado
        : typeof nested?.aprobado === "boolean"
          ? nested.aprobado
          : typeof grading?.aprobado === "boolean"
            ? grading.aprobado
            : pct != null
              ? pct >= Number(required || 70)
              : null;

    const correct = numberOrNull(
      pickFirstDefined(
        b?.respuestas_correctas,
        nested?.respuestas_correctas,
        grading?.correctas,
        detail.filter((x) => x?.correcto === true).length || null
      )
    );

    const incorrect = numberOrNull(
      pickFirstDefined(
        b?.respuestas_incorrectas,
        nested?.respuestas_incorrectas,
        grading?.incorrectas,
        detail.filter((x) => x?.correcto === false).length || null
      )
    );

    const unanswered = numberOrNull(
      pickFirstDefined(
        b?.respuestas_sin_responder,
        nested?.respuestas_sin_responder,
        grading?.sinResponder,
        grading?.sin_responder
      )
    );

    const autoGradables = numberOrNull(
      pickFirstDefined(
        b?.respuestas_autogradables,
        grading?.autoGradables,
        detail.length || null
      )
    );

    const answeredCount =
      detail.length ||
      Object.keys(normalizeObject(rawAnswers?.answers_by_question)).length ||
      Object.keys(normalizeObject(rawAnswers?.answers)).length ||
      null;

    const motivo = pickFirstDefined(
      rawAnswers?.motivo,
      nested?.motivo,
      b?.motivo_finalizacion
    );

    const proctorEstado = pickFirstDefined(
      b?.proctor_estado,
      nested?.proctor_estado
    );

    const proctorMotivo = pickFirstDefined(
      b?.proctor_motivo,
      nested?.proctor_motivo
    );

    return {
      pct,
      score,
      maxScore,
      approved,
      correct,
      incorrect,
      autoGradables,
      answeredCount,
      detail,
      rawAnswers,
      unanswered,
      grading,
      motivo,
      proctorEstado,
      proctorMotivo,
      finishedAt: pickFirstDefined(
        b?.presentacion_fecha_finalizacion,
        b?.fecha_finalizacion,
        nested?.fecha_finalizacion,
        rawAnswers?.finished_at
      ),
      startedAt: pickFirstDefined(
        b?.presentacion_fecha_inicio,
        b?.fecha_inicio,
        nested?.fecha_inicio
      ),
      presentationId: pickFirstDefined(
        b?.presentacion_id,
        nested?.presentacion_id,
        b?.id_presentacion
      ),
      required,
      timeSpent: numberOrNull(
        pickFirstDefined(
          b?.tiempo_empleado_segundos,
          b?.tiempo_empleado_segundos_calculado,
          nested?.tiempo_empleado_segundos,
          grading?.tiempo_empleado_segundos
        )
      ),
      attemptNumber: numberOrNull(
        pickFirstDefined(
          b?.numero_intento,
          b?.numero_intento_calculado,
          nested?.numero_intento
        )
      ),
      codeUsed: pickFirstDefined(
        b?.codigo_usado,
        nested?.codigo_usado
      ),
    };
  }

  function hasExamResult(b = {}) {
    const r = getResultPayload(b);
    return (
      isBookingFinal(b) ||
      r.pct != null ||
      r.score != null ||
      typeof r.approved === "boolean" ||
      String(b?.estado_presentacion || b?.presentacion?.estado_presentacion || "").toLowerCase() === "completado"
    );
  }

  const [loadingCourse, setLoadingCourse] = useState(true);
  const [bookingFilter, setBookingFilter] = useState("todos");
  const [bookingSearch, setBookingSearch] = useState("");
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [myPage, setMyPage] = useState(1);
  const [schedulePage, setSchedulePage] = useState(1);
  const PAGE_SIZE = 5;
  const [scheduleTypeFilter, setScheduleTypeFilter] = useState("todos");
  const [bookingViewMode, setBookingViewMode] = useState("table");
  const [scheduleViewMode, setScheduleViewMode] = useState("table");

  const [bookingSort, setBookingSort] = useState({
    key: "fecha",
    direction: "desc",
  });

  const [scheduleSort, setScheduleSort] = useState({
    key: "fecha",
    direction: "asc",
  });
  const [courseErr, setCourseErr] = useState("");
  const [course, setCourse] = useState(null);

  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [schedulesErr, setSchedulesErr] = useState("");
  const [schedules, setSchedules] = useState([]);

  const [myBookings, setMyBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const [showSchedulesPanel, setShowSchedulesPanel] = useState(true);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [scheduleDetail, setScheduleDetail] = useState(null);
  const [resultDetail, setResultDetail] = useState(null);
  const [accessCheck, setAccessCheck] = useState(null);
  const [infoModal, setInfoModal] = useState(null);

  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const toggleBookingSort = (key) => {
    setBookingSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleScheduleSort = (key) => {
    setScheduleSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortValue = (item, key, type) => {
    if (type === "booking") {
      if (key === "fecha") {
        return toDateTime(item?.fecha_examen, item?.hora_inicio)?.getTime() || 0;
      }

      if (key === "modalidad") {
        return item?.is_virtual ? "virtual" : "presencial";
      }

      if (key === "estado") {
        return statusUI(item).label || "";
      }

      if (key === "registro") {
        return getBookedAtMs(item) || 0;
      }
    }

    if (type === "schedule") {
      if (key === "fecha") {
        return toDateTime(item?.fecha_examen, item?.hora_inicio)?.getTime() || 0;
      }

      if (key === "modalidad") {
        return item?.is_virtual ? "virtual" : "presencial";
      }

      if (key === "cupos") {
        const ocupados = Number(item?.cupos_ocupados || 0);
        const cupos = Number(item?.cupos_disponibles || 0);
        return cupos - ocupados;
      }

      if (key === "ubicacion") {
        return scheduleLocationLabel(item) || "";
      }
    }

    return "";
  };

  const sortItems = (items, sortConfig, type) => {
    const dir = sortConfig.direction === "asc" ? 1 : -1;

    return [...items].sort((a, b) => {
      const av = getSortValue(a, sortConfig.key, type);
      const bv = getSortValue(b, sortConfig.key, type);

      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }

      return String(av).localeCompare(String(bv), "es", {
        sensitivity: "base",
        numeric: true,
      }) * dir;
    });
  };

  const renderSortHeader = (type, key, label) => {
    const config = type === "booking" ? bookingSort : scheduleSort;
    const active = config.key === key;

    return (
      <button
        type="button"
        className={`agx-sort-th ${active ? "is-active" : ""}`}
        onClick={() => {
          if (type === "booking") toggleBookingSort(key);
          else toggleScheduleSort(key);
        }}
      >
        <span>{label}</span>
        <SortIcon active={active} direction={config.direction} />
      </button>
    );
  };

  const filteredMyBookings = useMemo(() => {
    let list = [...(myBookings || [])];

    if (bookingFilter === "agendados") {
      list = list.filter((b) => isBookingActive(b, nowTs) || isBookingCancelled(b));
    } else if (bookingFilter === "evaluados") {
      list = list.filter((b) => isBookingFinal(b) || hasExamResult(b));
    } else if (bookingFilter === "cancelado") {
      list = list.filter((b) => isBookingCancelled(b));
    } else if (bookingFilter === "finalizado") {
      list = list.filter((b) => statusKey(b) === "finalizado" || hasExamResult(b));
    } else if (bookingFilter !== "todos") {
      list = list.filter((b) => isBookingActive(b, nowTs));
    }

    const term = bookingSearch.trim().toLowerCase();
    if (term) {
      list = list.filter((b) => {
        const bag = [
          formatDate(b.fecha_examen),
          formatTime(b.hora_inicio),
          formatTime(b.hora_fin),
          b.is_virtual ? "virtual" : "presencial",
          statusUI(b).label,
          scheduleLocationLabel(b),
          bookingAcademicState(b),
        ].join(" ").toLowerCase();

        return bag.includes(term);
      });
    }

    return sortItems(list, bookingSort, "booking");
  }, [myBookings, bookingFilter, bookingSearch, nowTs, bookingSort]);

  const totalPagesMyBookings = Math.max(1, Math.ceil(filteredMyBookings.length / PAGE_SIZE));
  const paginatedMyBookings = useMemo(() => {
    const safePage = Math.min(myPage, totalPagesMyBookings);
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredMyBookings.slice(start, start + PAGE_SIZE);
  }, [filteredMyBookings, myPage, totalPagesMyBookings]);

  useEffect(() => {
    setMyPage(1);
  }, [bookingFilter, bookingSearch]);

  const bookingCounts = useMemo(() => {
    const all = myBookings || [];
    return {
      all: all.length,
      active: all.filter((b) => isBookingActive(b, nowTs)).length,
      cancelled: all.filter((b) => isBookingCancelled(b)).length,
      finished: all.filter((b) => statusKey(b) === "finalizado").length,
    };
  }, [myBookings, nowTs]);

  const cooldownKey = useMemo(
    () => `exam_book_cooldown_${user?.id || "anon"}_${codigoCurso}`,
    [user?.id, codigoCurso]
  );

  const [bookCooldownUntil, setBookCooldownUntil] = useState(() => {
    try {
      const v = Number(localStorage.getItem(cooldownKey));
      return Number.isFinite(v) ? v : 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(cooldownKey, String(bookCooldownUntil || 0));
    } catch {
      //
    }
  }, [bookCooldownUntil, cooldownKey]);

  const cooldownActive = nowTs < bookCooldownUntil;
  const cooldownSecsLeft = Math.max(
    0,
    Math.ceil((bookCooldownUntil - nowTs) / 1000)
  );

  const [termsOpen, setTermsOpen] = useState(false);
  const [termsData, setTermsData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pendingSchedule, setPendingSchedule] = useState(null);

  const [cancelBusy, setCancelBusy] = useState({});

  const openTermsModal = async (schedule) => {
    try {
      setPendingSchedule(schedule);
      setTermsAccepted(false);
      const t = await fetchExamTermsCurrent?.();
      setTermsData(t);
      setTermsOpen(true);
    } catch (e) {
      setSchedulesErr(e?.message || "No se pudieron cargar los Términos y Condiciones.");
    }
  };

  const closeTermsModal = () => {
    setTermsOpen(false);
    setPendingSchedule(null);
    setTermsData(null);
    setTermsAccepted(false);
  };

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoadingCourse(true);
        setCourseErr("");
        const resolved = await fnsRef.current.resolveStudentCourseByCode?.(codigoCurso, {
          signal: ctrl.signal,
        });

        if (!alive) return;

        if (!resolved) {
          setCourseErr("Curso no encontrado o no estás inscrito");
          setCourse(null);
          return;
        }

        setCourse(resolved);
      } catch (e) {
        if (!alive) return;
        setCourseErr(e?.message || "No se pudo cargar el curso");
      } finally {
        if (alive) setLoadingCourse(false);
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [codigoCurso]);

  const courseCompleted = useMemo(() => {
    if (qaBypass) return true;
    if (!course?.id) return false;
    if (String(course?.tipo ?? "").toLowerCase() === "examen_solo") return true;

    const progress = Number(getCourseProgress?.(course.id) || 0);
    const min = toNum(
      course?.porcentaje_minimo_examen ??
      course?.porcentaje_minimo_aprobacion ??
      100,
      100
    );

    return Boolean(course?.completado) || progress >= min;
  }, [
    qaBypass,
    course?.id,
    course?.tipo,
    course?.completado,
    getCourseProgress,
    toNum,
  ]);

  const loadData = useCallback(
    async (cursoId, signal) => {
      if (!cursoId) {
        setSchedules([]);
        setMyBookings([]);
        return;
      }

      setLoadingSchedules(true);
      setSchedulesErr("");

      try {
        const [schedulesResp, bookingsAllResp] = await Promise.all([
          fnsRef.current.fetchCourseExamSchedules?.(Number(cursoId), {
            status: "activo",
            upcoming: false,
            signal,
          }),
          fnsRef.current.fetchMyExamBookings?.({
            curso_id: Number(cursoId),
            upcoming: false,
            user_id: user?.id,
            signal,
          }),
        ]);

        const items = Array.isArray(schedulesResp?.schedules)
          ? schedulesResp.schedules
          : [];

        items.sort((a, b) =>
          `${a.fecha_examen} ${a.hora_inicio}`.localeCompare(
            `${b.fecha_examen} ${b.hora_inicio}`
          )
        );
        setSchedules(items);

        const rawAll = Array.isArray(bookingsAllResp?.bookings)
          ? bookingsAllResp.bookings
          : bookingsAllResp?.items || [];

        const normalizedBookings = normalizeBookings(rawAll);
        setMyBookings(enrichBookingsWithSchedules(normalizedBookings, items));
      } catch (e) {
        setSchedules([]);
        setMyBookings([]);
        setSchedulesErr(e?.message || "No se pudieron cargar los horarios");
      } finally {
        setLoadingSchedules(false);
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (!course?.id) {
      setSchedules([]);
      setMyBookings([]);
      return;
    }
    const ctrl = new AbortController();
    loadData(course.id, ctrl.signal);
    return () => ctrl.abort();
  }, [course?.id, loadData]);

  const hasPassedFinal = useMemo(() => {
    return (myBookings || []).some((b) => {
      const isFinal = isBookingFinal(b);
      const aprobadoBool = typeof b?.aprobado === "boolean" ? b.aprobado : null;
      return isFinal && aprobadoBool === true;
    });
  }, [myBookings]);

  const alreadyBooked = useMemo(
    () => (myBookings || []).some((b) => isBookingActive(b, nowTs)),
    [myBookings, nowTs]
  );

  const anyOpenBooking = useMemo(
    () =>
      (myBookings || []).some(
        (b) =>
          isBookingActive(b, nowTs) &&
          isWindowOpenFor(b, nowTs) &&
          isStudentEnabledForExam(b)
      ),
    [myBookings, nowTs]
  );

  const canStartFromHere = Boolean(anyOpenBooking && !hasPassedFinal);

  const nextRelevantBooking = useMemo(() => {
    const active = (myBookings || []).filter((b) => isBookingActive(b, nowTs));
    if (!active.length) return null;

    const sorted = [...active].sort((a, b) => {
      const aStart = toDateTime(a.fecha_examen, a.hora_inicio)?.getTime() || 0;
      const bStart = toDateTime(b.fecha_examen, b.hora_inicio)?.getTime() || 0;
      return aStart - bStart;
    });

    return sorted[0];
  }, [myBookings, nowTs]);

  const gateAllowsStart =
    Boolean(gateState?.canStartExam && gateState?.isWindowOpen && !gateState?.hasCompleted) &&
    (!nextRelevantBooking || isStudentEnabledForExam(nextRelevantBooking));

  const effectiveCanStart = gateAllowsStart || canStartFromHere;


  const summaryStatus = useMemo(() => {
    if (loadingCourse) return { label: "Cargando", cls: "ag-status-chip ag-status-chip-gray" };
    if (courseErr) return { label: "No disponible", cls: "ag-status-chip ag-status-chip-gray" };
    if (hasPassedFinal) return { label: "Aprobado", cls: "ag-status-chip ag-status-chip-green" };
    if (canStartFromHere)
      return { label: "Disponible", cls: "ag-status-chip ag-status-chip-blue" };
    if (alreadyBooked) return { label: "Agendado", cls: "ag-status-chip ag-status-chip-blue" };
    if (courseCompleted)
      return { label: "Listo", cls: "ag-status-chip ag-status-chip-green" };
    return { label: "Pendiente", cls: "ag-status-chip ag-status-chip-gray" };
  }, [
    loadingCourse,
    courseErr,
    hasPassedFinal,
    canStartFromHere,
    alreadyBooked,
    courseCompleted,
  ]);

  const nextRelevantLabel = useMemo(() => {
    if (!nextRelevantBooking) return "Sin registros próximos";
    if (isWindowOpenFor(nextRelevantBooking, nowTs) && !isStudentEnabledForExam(nextRelevantBooking)) {
      return "Pendiente de habilitación";
    }
    if (isWindowOpenFor(nextRelevantBooking, nowTs)) return "Disponible ahora";
    const ms = msUntilWindowStart(nextRelevantBooking, nowTs);
    if (ms != null && ms > 0) return `Inicia en ${msToNice(ms)}`;
    return "Agendado";
  }, [nextRelevantBooking, nowTs]);

  const myBookedScheduleKeys = useMemo(() => {
    const ids = new Set();
    const keys = new Set();

    (myBookings || []).forEach((b) => {
      if (!isBookingActive(b, nowTs)) return;

      const hid = Number(b?.horario_examen_id);
      if (Number.isFinite(hid)) ids.add(hid);

      const d = dateOnly(b?.fecha_examen);
      const hi = hhmmOnly(b?.hora_inicio);
      const hf = hhmmOnly(b?.hora_fin);
      if (d && hi && hf) keys.add(`${d}|${hi}|${hf}`);
    });

    return { ids, keys };
  }, [myBookings, nowTs]);

  const myConsumedScheduleKeys = useMemo(() => {
    const ids = new Set();
    const keys = new Set();

    (myBookings || []).forEach((b) => {
      if (!isBookingFinal(b)) return;

      const hid = Number(b?.horario_examen_id);
      if (Number.isFinite(hid)) ids.add(hid);

      const d = dateOnly(b?.fecha_examen);
      const hi = hhmmOnly(b?.hora_inicio);
      const hf = hhmmOnly(b?.hora_fin);
      if (d && hi && hf) keys.add(`${d}|${hi}|${hf}`);
    });

    return { ids, keys };
  }, [myBookings]);

  const visibleSchedules = useMemo(() => {
    let list = (schedules || [])
      .filter((h) => {
        if (scheduleTypeFilter === "virtual") return !!h.is_virtual;
        if (scheduleTypeFilter === "presencial") return !h.is_virtual;
        return true;
      })
      .filter((h) => {
        if (isSchedulePast(h, nowTs)) return false;

        const hid = Number(h?.id);
        if (Number.isFinite(hid) && myBookedScheduleKeys.ids.has(hid)) return false;
        if (Number.isFinite(hid) && myConsumedScheduleKeys.ids.has(hid)) return false;

        const d = dateOnly(h?.fecha_examen);
        const hi = hhmmOnly(h?.hora_inicio);
        const hf = hhmmOnly(h?.hora_fin);

        if (d && hi && hf) {
          const key = `${d}|${hi}|${hf}`;
          if (myBookedScheduleKeys.keys.has(key)) return false;
          if (myConsumedScheduleKeys.keys.has(key)) return false;
        }

        const term = scheduleSearch.trim().toLowerCase();
        if (term) {
          const bag = [
            formatDate(h.fecha_examen),
            formatTime(h.hora_inicio),
            formatTime(h.hora_fin),
            h.is_virtual ? "virtual" : "presencial",
            scheduleLocationLabel(h),
          ].join(" ").toLowerCase();

          if (!bag.includes(term)) return false;
        }

        return true;
      });

    return sortItems(list, scheduleSort, "schedule");
  }, [
    schedules,
    scheduleTypeFilter,
    scheduleSearch,
    myBookedScheduleKeys,
    myConsumedScheduleKeys,
    nowTs,
    scheduleSort,
  ]);

  const totalPagesSchedules = Math.max(1, Math.ceil(visibleSchedules.length / PAGE_SIZE));
  const paginatedSchedules = useMemo(() => {
    const safePage = Math.min(schedulePage, totalPagesSchedules);
    const start = (safePage - 1) * PAGE_SIZE;
    return visibleSchedules.slice(start, start + PAGE_SIZE);
  }, [visibleSchedules, schedulePage, totalPagesSchedules]);

  useEffect(() => {
    setSchedulePage(1);
  }, [scheduleTypeFilter, scheduleSearch]);

  const handleVerifyAccess = (b) => {
    if (!b || isBookingCancelled(b) || isBookingFinal(b)) return;

    if (!isWindowOpenFor(b, nowTs)) {
      setAccessCheck(b);
      return;
    }

    startExamForBooking(b);
  };

  const startExamForBooking = (b) => {
    const bid = ensureNum(b?.booking_id);
    const horarioId = ensureNum(b?.horario_examen_id ?? b?.horario_id);

    try {
      sessionStorage.setItem(
        `exam:start-intent:${codigoCurso}`,
        JSON.stringify({
          examStart: true,
          booking_id: bid,
          horario_id: horarioId,
          ts: Date.now(),
        })
      )
    } catch { }

    navigate(`/curso/${codigoCurso}/examen`, {
      state: {
        examStart: true,
        booking_id: bid,
        horario_id: horarioId,
      },
    });
  };

  const toggleSchedulesPanel = () => {
    setShowSchedulesPanel(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        schedulesSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  };

  const handleBook = async (schedule) => {
    if (!schedule?.id || busy) return;

    if (hasPassedFinal) {
      setSchedulesErr("Ya aprobaste este examen. No puedes volver a agendar en este curso.");
      return;
    }

    if (!courseCompleted || alreadyBooked) return;

    if (cooldownActive) {
      setSchedulesErr(`Debes esperar ${cooldownSecsLeft}s para volver a agendar.`);
      return;
    }

    if (!user?.id) {
      setSchedulesErr("No se encontró el usuario en sesión.");
      return;
    }

    if (requiresTerms(schedule)) {
      await openTermsModal(schedule);
      return;
    }

    setBusy(true);
    setToast(null);
    setSchedulesErr("");

    const tempId = `tmp-${Date.now()}`;
    const bookedTs = Date.now();

    const controlUnitario = !!schedule?.habilitacion_unitaria_estudiantes;
    const habilitadoInicial = !controlUnitario;

    setMyBookings((prev) => [
      {
        booking_id: tempId,
        fecha_examen: schedule.fecha_examen,
        hora_inicio: schedule.hora_inicio,
        hora_fin: schedule.hora_fin,
        is_virtual: !!schedule.is_virtual,
        habilitado: habilitadoInicial,
        habilitacion_unitaria_estudiantes: controlUnitario,
        estado: "agendado",
        booking_status: "agendado",
        horario_examen_id: schedule.id,
        agendado_en_ts: bookedTs,
      },
      ...prev,
    ]);

    setSchedules((prev) =>
      prev.map((h) =>
        h.id === schedule.id
          ? { ...h, cupos_ocupados: Number(h.cupos_ocupados || 0) + 1 }
          : h
      )
    );

    try {
      const created = await selfScheduleExam?.(Number(schedule.id), Number(user.id), false);
      const realId = pickBookingId(created);

      if (realId != null) {
        setMyBookings((prev) =>
          prev.map((x) =>
            x.booking_id === tempId
              ? {
                ...x,
                booking_id: realId,
                id: realId,
                habilitado:
                  created?.habilitado ??
                  created?.agendamiento?.habilitado ??
                  habilitadoInicial,
                habilitacion_unitaria_estudiantes:
                  created?.habilitacion_unitaria_estudiantes ??
                  created?.agendamiento?.habilitacion_unitaria_estudiantes ??
                  controlUnitario,
              }
              : x
          )
        );
      }

      setToast({ msg: "Te agendaste con éxito", kind: "success" });
      setShowSchedulesPanel(true);
    } catch (e) {
      setMyBookings((prev) => prev.filter((x) => x.booking_id !== tempId));
      setSchedules((prev) =>
        prev.map((h) =>
          h.id === schedule.id
            ? {
              ...h,
              cupos_ocupados: Math.max(0, Number(h.cupos_ocupados || 0) - 1),
            }
            : h
        )
      );
      setSchedulesErr(e?.message || "No se pudo completar el agendamiento");
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 2500);
    }
  };

  const confirmTermsAndBook = async () => {
    if (!pendingSchedule || !termsAccepted || busy) return;

    if (cooldownActive) {
      setSchedulesErr(`Debes esperar ${cooldownSecsLeft}s para volver a agendar.`);
      return;
    }

    setBusy(true);
    setToast(null);
    setSchedulesErr("");

    const s = pendingSchedule;
    const tempId = `tmp-${Date.now()}`;
    const bookedTs = Date.now();

    setMyBookings((prev) => [
      {
        booking_id: tempId,
        fecha_examen: s.fecha_examen,
        hora_inicio: s.hora_inicio,
        hora_fin: s.hora_fin,
        is_virtual: !!s.is_virtual,
        estado: "agendado",
        habilitado: !boolish(s?.habilitacion_unitaria_estudiantes),
        habilitacion_unitaria_estudiantes: boolish(s?.habilitacion_unitaria_estudiantes),
        booking_status: "agendado",
        horario_examen_id: s.id,
        agendado_en_ts: bookedTs,
      },
      ...prev,
    ]);

    setSchedules((prev) =>
      prev.map((h) =>
        h.id === s.id
          ? { ...h, cupos_ocupados: Number(h.cupos_ocupados || 0) + 1 }
          : h
      )
    );

    try {
      const created = await selfScheduleExam?.(Number(s.id), Number(user.id), true);
      const realId = pickBookingId(created);

      if (realId != null) {
        setMyBookings((prev) =>
          prev.map((x) =>
            x.booking_id === tempId
              ? {
                ...x,
                booking_id: realId,
                id: realId,
                habilitado:
                  created?.habilitado ??
                  created?.agendamiento?.habilitado ??
                  !boolish(s?.habilitacion_unitaria_estudiantes),
                habilitacion_unitaria_estudiantes:
                  created?.habilitacion_unitaria_estudiantes ??
                  created?.agendamiento?.habilitacion_unitaria_estudiantes ??
                  boolish(s?.habilitacion_unitaria_estudiantes),
              }
              : x
          )
        );
      }

      setToast({ msg: "Te agendaste con éxito", kind: "success" });
      setShowSchedulesPanel(true);
      closeTermsModal();
    } catch (e) {
      setMyBookings((prev) => prev.filter((x) => x.booking_id !== tempId));
      setSchedules((prev) =>
        prev.map((h) =>
          h.id === s.id
            ? { ...h, cupos_ocupados: Math.max(0, Number(h.cupos_ocupados || 0) - 1) }
            : h
        )
      );
      setSchedulesErr(e?.message || "No se pudo completar el agendamiento");
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 2500);
    }
  };

  const handleCancelBooking = async (b) => {
    const busyKey = String(
      b.booking_id ??
      `hid:${b.horario_examen_id || ""}:${b.fecha_examen || ""}:${b.hora_inicio || ""}`
    );

    if (cancelBusy[busyKey] || busy) return;

    if (isWindowOpenFor(b, nowTs)) {
      setSchedulesErr("No puedes cancelar porque el examen ya ha iniciado.");
      return;
    }

    if (
      !window.confirm(
        `¿Cancelar tu agendamiento del ${formatDate(b.fecha_examen)} de ${formatTime(
          b.hora_inicio
        )} a ${formatTime(b.hora_fin)}?`
      )
    ) {
      return;
    }

    setCancelBusy((m) => ({ ...m, [busyKey]: true }));
    setToast(null);
    setSchedulesErr("");

    const bid = ensureNum(b.booking_id);
    const previousStatus = getStatusRaw(b) || "agendado";

    if (bid == null) {
      setCancelBusy((m) => ({ ...m, [busyKey]: false }));
      setSchedulesErr(
        "Aún estamos confirmando tu agendamiento. Intenta cancelar nuevamente en unos segundos."
      );
      return;
    }

    setMyBookings((prev) =>
      prev.map((x) =>
        ensureNum(x.booking_id) === bid
          ? { ...x, estado: "cancelado", booking_status: "cancelado" }
          : x
      )
    );

    try {
      await fnsRef.current.cancelMyExamBooking?.(bid, Number(user.id));

      const hid = Number(b?.horario_examen_id);
      if (Number.isFinite(hid)) {
        setSchedules((prev) =>
          prev.map((h) =>
            Number(h?.id) === hid
              ? { ...h, cupos_ocupados: Math.max(0, Number(h.cupos_ocupados || 0) - 1) }
              : h
          )
        );
      }

      if (course?.id) {
        const ctrl = new AbortController();
        loadData(course.id, ctrl.signal);
      }

      setToast({ msg: "Agendamiento cancelado con éxito", kind: "success" });
      setBookCooldownUntil(Date.now() + COOLDOWN_MS);
    } catch (e) {
      setMyBookings((prev) =>
        prev.map((x) =>
          ensureNum(x.booking_id) === bid
            ? { ...x, estado: previousStatus, booking_status: previousStatus }
            : x
        )
      );
      setSchedulesErr(e?.message || "No se pudo cancelar el agendamiento.");
    } finally {
      setCancelBusy((m) => ({ ...m, [busyKey]: false }));
      setTimeout(() => setToast(null), 2500);
    }
  };

  return (
    <div className="agx-page">
      <style>{`
        :root{
          --agx-bg:#f5f7fb;
          --agx-card:#ffffff;
          --agx-border:#e7edf5;
          --agx-border-strong:#d7e1ef;
          --agx-text:#24364b;
          --agx-title:#14304a;
          --agx-muted:#6f8196;
          --agx-muted-2:#95a4b5;

          --agx-primary:#6f8fb3;
          --agx-primary-strong:#5d7fa5;
          --agx-primary-soft:#edf5ff;
          --agx-primary-soft-2:#f4f8fd;

          --agx-blue:#5a8fd6;
          --agx-blue-soft:#eaf3ff;
          --agx-blue-border:#cfe1fb;

          --agx-green:#6ec88e;
          --agx-green-soft:#e9f8ee;
          --agx-green-text:#37945b;

          --agx-purple:#b88af3;
          --agx-purple-soft:#f3eafe;
          --agx-purple-text:#8854d0;

          --agx-red:#e98383;
          --agx-red-soft:#fff1f1;
          --agx-red-text:#c55454;

          --agx-gray-pill:#f3f5f8;
          --agx-gray-pill-text:#718197;

          --agx-dark:#182538;
          --agx-dark-soft:#eef2f7;

          --agx-shadow:0 14px 30px rgba(32, 58, 92, .06);
          --agx-shadow-soft:0 8px 18px rgba(32, 58, 92, .05);
          --agx-radius:24px;
        }

        *{ box-sizing:border-box; }

        .agx-page{
          width:100%;
          max-width:1160px;
          margin:0 auto;
          padding:24px 14px 40px;
          color:var(--agx-text);
        }

        @media (min-width: 768px){
          .agx-page{ padding:28px 18px 48px; }
        }

        .agx-card{
          background:var(--agx-card);
          border:1px solid var(--agx-border);
          border-radius:var(--agx-radius);
          box-shadow:var(--agx-shadow);
        }

        .agx-top-card{
          padding:20px;
        }

        .agx-top-layout{
          display:flex;
          gap:18px;
          align-items:flex-start;
          justify-content:space-between;
          flex-wrap:wrap;
        }

        .agx-top-main{
          display:flex;
          align-items:center;
          gap:18px;
          min-width:0;
          flex:1 1 460px;
        }

        .agx-top-icon{
          width:88px;
          height:88px;
          border-radius:22px;
          background:linear-gradient(180deg,#edf5ff,#f6faff);
          border:1px solid #dbe9fb;
          display:grid;
          place-items:center;
          color:#4f7fc5;
          box-shadow:inset 0 1px 0 rgba(255,255,255,.75);
          flex:0 0 auto;
        }

        .ag-svg-icon{
          width:38px;
          height:38px;
          display:block;
        }

        .agx-top-copy{
          min-width:0;
        }

        .agx-kicker{
          margin:0 0 8px;
          color:#9daabd;
          font-size:12px;
          font-weight:900;
          letter-spacing:.08em;
          text-transform:uppercase;
        }

        .agx-title{
          margin:0;
          color:var(--agx-title);
          font-size:clamp(28px, 3vw, 42px);
          line-height:1;
          font-weight:950;
          text-transform:uppercase;
          word-break:break-word;
        }

        .agx-subtitle{
          margin-top:10px;
          color:#58708d;
          font-size:15px;
          font-weight:800;
        }

       .agx-side{
  display:flex;
  flex-direction:column;
  gap:8px;
  align-items:flex-end;
  min-width:160px;
  max-width:190px;
  flex:0 0 auto;
}

.agx-panel-shell{
  overflow:hidden;
  transition:
    max-height .38s ease,
    opacity .24s ease,
    transform .24s ease,
    padding-top .24s ease;
  will-change:max-height, opacity, transform;
}

.agx-panel-shell.is-open{
  max-height: 2200px;
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.agx-primary-btn-enter{
  min-height:36px;
  padding:0 11px;
  font-size:11.5px;
  border-radius:11px;
  box-shadow:0 5px 10px rgba(98,133,170,.12);
}

.agx-head-actions .agx-primary-btn{
  align-self:center;
}

.agx-head-actions{
  gap:8px;
}

.agx-panel-shell.is-closed{
  max-height: 0;
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
}

.agx-panel-empty{
  overflow:hidden;
  transition:
    max-height .28s ease,
    opacity .2s ease,
    transform .2s ease,
    margin .2s ease;
}

.agx-panel-empty.is-open{
  max-height: 140px;
  opacity: 1;
  transform: translateY(0);
  margin-top: 0;
}

.agx-panel-empty.is-closed{
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
  margin-top: 0;
}

        .agx-hero-btn{
  min-height:44px;
  border-radius:14px;
  border:1px solid transparent;
  padding:0 14px;
  font-size:13px;
  font-weight:800;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  cursor:pointer;
  transition:.18s ease;
  line-height:1;
}

        .agx-hero-btn:disabled{
          cursor:not-allowed;
          opacity:.65;
        }

        .agx-hero-btn-primary{
          background:var(--agx-blue-soft);
          color:#3271ce;
          border-color:var(--agx-blue-border);
          box-shadow:var(--agx-shadow-soft);
        }
.agx-hero-btn-secondary{
  background:#fff;
  color:#a1adbb;
  border-color:#edf1f6;
  width:auto;
  max-width:170px;
  white-space:nowrap;
}

        .agx-inline-icon{
          width:18px;
          height:18px;
          display:block;
          flex:0 0 auto;
        }

        .agx-inline-icon-sm{
          width:16px;
          height:16px;
        }

       .ag-status-chip{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-height:32px;
  padding:0 10px;
  border-radius:10px;
  font-size:11px;
  font-weight:800;
  border:1px solid transparent;
  text-align:center;
  line-height:1;
  width:auto;
  align-self:flex-end;
}

        .ag-status-chip-blue{
          background:var(--agx-blue-soft);
          color:#3271ce;
          border-color:var(--agx-blue-border);
        }

        .ag-status-chip-green{
          background:var(--agx-green-soft);
          color:var(--agx-green-text);
          border-color:#caeed7;
        }

        .ag-status-chip-gray{
          background:#f7f9fc;
          color:#708095;
          border-color:#e8edf4;
        }

        .agx-alerts{
          display:grid;
          gap:10px;
          margin-top:14px;
        }

        .agx-alert{
          border-radius:16px;
          padding:12px 14px;
          font-size:13px;
          font-weight:800;
          border:1px solid;
          display:flex;
          gap:10px;
          align-items:flex-start;
        }

        .agx-alert-info{
          background:#eef6ff;
          border-color:#d6e6fb;
          color:#316fc9;
        }

        .agx-alert-success{
          background:#edf9f0;
          border-color:#d3efd9;
          color:#2d8b53;
        }

        .agx-alert-error{
          background:#fff2f2;
          border-color:#f8d8d8;
          color:#c15656;
        }

        .agx-main-card{
          margin-top:22px;
          overflow:hidden;
        }

        .agx-main-head{
          padding:26px 26px 16px;
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          flex-wrap:wrap;
        }

        .agx-main-title{
          margin:0;
          color:var(--agx-title);
          font-size:22px;
          font-weight:950;
          letter-spacing:-.02em;
        }

        .agx-main-sub{
          margin:6px 0 0;
          color:var(--agx-muted);
          font-size:15px;
          font-weight:700;
        }

       .agx-head-actions{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  justify-content:flex-end;
}

       .agx-help-btn{
  width:38px;
  height:38px;
  border-radius:999px;
  border:1px solid var(--agx-blue-border);
  background:var(--agx-blue-soft);
  color:#4a82d7;
  display:grid;
  place-items:center;
  cursor:pointer;
  box-shadow:var(--agx-shadow-soft);
}

      .agx-primary-btn{
  min-height:38px;
  border:none;
  border-radius:12px;
  background:linear-gradient(180deg,#6f8fb3,#5f80a3);
  color:#fff;
  padding:0 12px;
  font-size:12px;
  font-weight:800;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  cursor:pointer;
  box-shadow:0 6px 12px rgba(98,133,170,.14);
  transition:background .18s ease, transform .18s ease, box-shadow .18s ease, opacity .18s ease;
  line-height:1;
}

        .agx-primary-btn:disabled{
          opacity:.65;
          cursor:not-allowed;
        }

        .agx-primary-btn:hover:not(:disabled),
.agx-hero-btn:hover:not(:disabled){
  transform:translateY(-1px);
}

.agx-primary-btn[aria-expanded="true"]{
  background:linear-gradient(180deg,#5f83aa,#547392);
  box-shadow:0 5px 10px rgba(84,115,146,.16);
}

        .agx-toolbar{
          padding:0 26px 18px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:16px;
          flex-wrap:wrap;
        }

        .agx-filters{
          display:flex;
          gap:10px;
          flex-wrap:wrap;
        }

        .agx-filter{
  min-height:36px;
  padding:0 14px;
  border-radius:12px;
  border:1px solid var(--agx-border-strong);
  background:#fff;
  color:#5f738a;
  font-size:12px;
  font-weight:800;
  cursor:pointer;
  transition:.16s ease;
  line-height:1;
}

        .agx-filter:hover{
          background:#fafcff;
          border-color:#c9d6e5;
        }

        .agx-filter.is-active-dark{
          background:var(--agx-dark);
          border-color:var(--agx-dark);
          color:#fff;
          box-shadow:0 8px 18px rgba(24,37,56,.18);
        }

        .agx-filter.is-active-blue{
          background:var(--agx-blue-soft);
          border-color:var(--agx-blue-border);
          color:#3573cf;
        }

        .agx-filter.is-active-green{
          background:var(--agx-green-soft);
          border-color:#caeed7;
          color:var(--agx-green-text);
        }

        .agx-filter.is-active-red{
          background:var(--agx-red-soft);
          border-color:#f3d1d1;
          color:var(--agx-red-text);
        }

        .agx-table-wrap{
          border-top:1px solid var(--agx-border);
          background:#fff;
        }

        .agx-table{
          width:100%;
          border-collapse:collapse;
        }

        .agx-table thead th{
  padding:14px 18px;
  text-align:left;
  color:#72839a;
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:.05em;
  background:#fff;
  border-bottom:1px solid var(--agx-border);
  white-space:nowrap;
}

.agx-table tbody td{
  padding:16px 18px;
  border-bottom:1px solid #edf2f7;
  vertical-align:middle;
}

        .agx-table tbody tr:last-child td{
          border-bottom:none;
        }

        .agx-date{
          font-size:14px;
          color:var(--agx-title);
          font-weight:900;
          line-height:1.35;
        }

        .agx-time{
          margin-top:4px;
          font-size:12px;
          color:var(--agx-muted);
          font-weight:700;
        }

        .agx-place{
          margin-top:6px;
          font-size:12px;
          color:#8c9aae;
          font-weight:700;
        }

        .ag-pill{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-height:28px;
  padding:0 10px;
  border-radius:999px;
  border:1px solid transparent;
  font-size:11px;
  font-weight:800;
  white-space:nowrap;
  line-height:1;
}

        .ag-pill-blue{
          background:var(--agx-blue-soft);
          color:#3d78cf;
          border-color:var(--agx-blue-border);
        }

        .ag-pill-green{
          background:var(--agx-green-soft);
          color:var(--agx-green-text);
          border-color:#caeed7;
        }

        .ag-pill-purple{
          background:var(--agx-purple-soft);
          color:var(--agx-purple-text);
          border-color:#e5d6fc;
        }

        .ag-pill-red{
          background:var(--agx-red-soft);
          color:var(--agx-red-text);
          border-color:#f3d1d1;
        }

        .ag-pill-gray{
          background:var(--agx-gray-pill);
          color:var(--agx-gray-pill-text);
          border-color:#e6ebf1;
        }

        .ag-pill-amber{
          background:#fff5e7;
          color:#b7771b;
          border-color:#f5ddb0;
        }

        .agx-cell-stack{
          display:flex;
          flex-direction:column;
          align-items:flex-start;
          gap:8px;
        }

        .agx-action-row{
          display:flex;
          gap:10px;
          flex-wrap:wrap;
          align-items:center;
        }

       .agx-btn{
  min-height:32px;
  padding:0 10px;
  border-radius:10px;
  border:1px solid transparent;
  font-size:11px;
  font-weight:800;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  cursor:pointer;
  background:#fff;
  transition:background .16s ease, border-color .16s ease, color .16s ease, transform .16s ease, box-shadow .16s ease;
  line-height:1;
}

        .agx-btn:disabled{
          opacity:.6;
          cursor:not-allowed;
        }

        .agx-btn-ghost{
          color:#57708d;
          border-color:#dbe5f1;
          background:#fff;
          box-shadow:var(--agx-shadow-soft);
        }

        .agx-btn-soft{
          color:#8e99a8;
          background:#f8fafc;
          border-color:#edf2f7;
        }

        .agx-btn-enter{
  color:#245fb8;
  background:linear-gradient(180deg,#eef5ff,#e3efff);
  border-color:#cfe0fb;
  box-shadow:0 4px 10px rgba(90,143,214,.12);
}

.agx-btn-enter:hover:not(:disabled){
  background:linear-gradient(180deg,#e7f1ff,#dceaff);
  border-color:#bdd4f7;
  color:#1f56ab;
  transform:translateY(-1px);
}

.agx-btn-enter:active:not(:disabled){
  transform:translateY(0);
  box-shadow:0 2px 6px rgba(90,143,214,.12);
}

        .agx-btn-danger{
          color:#d16666;
          background:var(--agx-red-soft);
          border-color:#f3d1d1;
        }

        .agx-btn-primary{
          color:#fff;
          background:linear-gradient(180deg,#7090b3,#6285aa);
          border-color:transparent;
          box-shadow:0 10px 20px rgba(98,133,170,.22);
        }

        .agx-empty{
          margin:18px 26px 26px;
          border:1px dashed #d8e2ef;
          border-radius:18px;
          background:#fbfdff;
          padding:22px;
          text-align:center;
          font-size:14px;
          font-weight:800;
          color:#6d8097;
        }

        .agx-mobile-list{
          display:grid;
          gap:14px;
          padding:0 16px 16px;
        }

        .agx-mobile-card{
          background:#fff;
          border:1px solid var(--agx-border);
          border-radius:18px;
          padding:16px;
          box-shadow:var(--agx-shadow-soft);
        }

        .agx-mobile-top{
          display:flex;
          justify-content:space-between;
          gap:12px;
          align-items:flex-start;
          flex-wrap:wrap;
        }

        .agx-mobile-meta{
          margin-top:10px;
          display:flex;
          gap:8px;
          flex-wrap:wrap;
        }

        .agx-mobile-actions{
          margin-top:14px;
          display:flex;
          gap:10px;
          flex-wrap:wrap;
        }

        .agx-hidden-panel{
          padding:0 26px 26px;
        }

        .agx-separator{
          height:1px;
          background:#edf2f7;
          margin:4px 26px 0;
        }

        .agx-schedules-head{
          padding:24px 26px 0;
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          flex-wrap:wrap;
        }

        .agx-schedules-title{
          margin:0;
          color:var(--agx-title);
          font-size:20px;
          font-weight:950;
        }

        .agx-schedules-sub{
          margin:6px 0 0;
          color:var(--agx-muted);
          font-size:14px;
          font-weight:700;
        }

        .agx-schedule-card{
          border:1px solid var(--agx-border);
          background:#fff;
          border-radius:18px;
          padding:16px;
        }

        .agx-progress{
          width:140px;
          max-width:100%;
          height:8px;
          border-radius:999px;
          background:#e8eef6;
          overflow:hidden;
          margin-top:8px;
        }

        .agx-progress > span{
          display:block;
          height:100%;
          border-radius:999px;
        }

        .agx-note{
          color:#9babbd;
          font-size:12px;
          font-weight:800;
        }

        .agx-modal-backdrop{
          position:fixed;
          inset:0;
          z-index:90;
          padding:16px;
          background:rgba(20,35,54,.42);
          backdrop-filter:blur(4px);
          display:grid;
          place-items:center;
        }

        .agx-modal{
          width:min(760px, 96vw);
          max-height:88vh;
          overflow:auto;
          background:#fff;
          border-radius:24px;
          border:1px solid var(--agx-border);
          box-shadow:0 24px 60px rgba(20,35,54,.22);
        }

        .agx-modal-head{
          display:flex;
          justify-content:space-between;
          gap:12px;
          align-items:flex-start;
          padding:20px 20px 0;
        }

        .agx-modal-title{
          margin:0;
          color:var(--agx-title);
          font-size:22px;
          font-weight:950;
        }

        .agx-modal-sub{
          margin:6px 0 0;
          color:var(--agx-muted);
          font-size:13px;
          font-weight:700;
        }

        .agx-modal-body{
          padding:20px;
        }

        .agx-detail-grid{
          display:grid;
          grid-template-columns:repeat(1,minmax(0,1fr));
          gap:12px;
        }

        @media (min-width: 640px){
          .agx-detail-grid{
            grid-template-columns:repeat(2,minmax(0,1fr));
          }
        }

        .agx-detail-item{
          border:1px solid var(--agx-border);
          background:#fbfdff;
          border-radius:16px;
          padding:14px;
        }

        .agx-detail-label{
          font-size:11px;
          color:#97a6b7;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.06em;
          margin-bottom:8px;
        }

        .agx-detail-value{
          color:var(--agx-title);
          font-size:14px;
          font-weight:900;
          line-height:1.35;
        }

        .agx-detail-sub{
          margin-top:6px;
          color:var(--agx-muted);
          font-size:12px;
          font-weight:700;
          line-height:1.35;
        }

        .agx-terms-box{
          border:1px solid var(--agx-border);
          background:#fbfdff;
          border-radius:16px;
          padding:14px;
          white-space:pre-wrap;
          color:#3a4d63;
          font-size:14px;
          line-height:1.55;
        }

        .agx-check{
          margin-top:14px;
          display:flex;
          gap:10px;
          align-items:flex-start;
          font-size:14px;
          color:var(--agx-title);
          font-weight:800;
        }

        .agx-check input{
          margin-top:2px;
        }

        .desktop-only{ display:none; }
        .mobile-only{ display:block; }

        @media (min-width: 960px){
          .desktop-only{ display:block; }
          .mobile-only{ display:none; }
        }

        @media (max-width: 640px){
          .agx-top-card,
          .agx-main-head,
          .agx-toolbar,
          .agx-schedules-head{
            padding-left:16px;
            padding-right:16px;
          }

          .agx-hidden-panel{
            padding-left:16px;
            padding-right:16px;
          }

          .agx-separator{
            margin-left:16px;
            margin-right:16px;
          }

          .agx-empty{
            margin-left:16px;
            margin-right:16px;
          }

          .agx-side{
            width:100%;
            min-width:0;
          }

          .agx-title-inline{
            width:100%;
            justify-content:space-between;
          }
        }

        .agx-result-hero{
          border:1px solid var(--agx-border);
          background:linear-gradient(180deg,#ffffff,#f8fbff);
          border-radius:20px;
          padding:18px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:14px;
          flex-wrap:wrap;
        }

        .agx-result-title{
          margin-top:4px;
          color:var(--agx-title);
          font-size:28px;
          font-weight:950;
          letter-spacing:-.03em;
        }

        .agx-result-list{
          margin-top:16px;
          border:1px solid var(--agx-border);
          border-radius:18px;
          padding:14px;
          background:#fff;
          display:grid;
          gap:10px;
        }

        .agx-result-row{
          display:flex;
          gap:10px;
          align-items:flex-start;
          color:#52677f;
          font-size:13px;
          font-weight:700;
          border-top:1px solid #f0f3f8;
          padding-top:10px;
        }



        .agx-search-box{
          min-width:230px;
          height:34px;
          display:flex;
          align-items:center;
          gap:8px;
          padding:0 12px;
          border:1px solid var(--agx-border-strong);
          border-radius:12px;
          background:#fff;
          color:#94a3b8;
        }
        .agx-search-box input{
          border:0;
          outline:0;
          width:100%;
          font-size:12px;
          color:var(--agx-text);
          background:transparent;
          font-weight:700;
        }
        .agx-search-box input::placeholder{ color:#a6b3c2; font-weight:700; }
        .agx-pager{
          display:flex;
          align-items:center;
          gap:8px;
          padding:12px 4px 2px;
          color:#7f8da1;
          font-size:12px;
          font-weight:800;
        }
        .agx-pager button{
          width:28px;
          height:28px;
          border-radius:10px;
          border:1px solid var(--agx-border);
          background:#fff;
          color:#64748b;
          font-size:18px;
          line-height:1;
          cursor:pointer;
        }
        .agx-pager button:disabled{ opacity:.35; cursor:not-allowed; }
        .agx-dark-head{
          background:#1e2a3a;
          color:#fff;
          padding:22px 24px;
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
        }
        .agx-modal-title-light{ color:#fff !important; }
        .agx-modal-sub-light{ color:#dbe8ff !important; }
        .agx-round-close{
          width:34px;
          height:34px;
          border:0;
          border-radius:999px;
          background:rgba(255,255,255,.13);
          color:#fff;
          display:grid;
          place-items:center;
          cursor:pointer;
        }
        .agx-config-modal{ max-width:540px; overflow:hidden; padding:0; }
        .agx-verification-modal{ max-width:560px; overflow:hidden; padding:0; }
        .agx-config-grid{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:14px;
        }
        .agx-config-item{
          display:flex;
          gap:12px;
          align-items:center;
          min-height:72px;
          padding:16px;
          border:1px solid var(--agx-border);
          border-radius:16px;
          background:#f8fafc;
        }
        .agx-config-icon{
          color:#2f6fee;
          width:24px;
          display:grid;
          place-items:center;
          font-weight:900;
        }
        .agx-config-icon.agx-ok{ color:#22c55e; }
        .agx-config-link{
          margin-top:16px;
          display:flex;
          align-items:center;
          gap:12px;
          padding:16px;
          border-radius:16px;
          border:1px solid #cfe1fb;
          background:#f1f7ff;
          color:#2463d6;
        }
        .agx-access-card{
          display:flex;
          align-items:center;
          gap:14px;
          padding:16px;
          border:1px solid #cfe1fb;
          border-radius:18px;
          background:#f1f7ff;
        }
        .agx-access-icon{
          width:42px;
          height:42px;
          border-radius:14px;
          display:grid;
          place-items:center;
          color:#2463d6;
          background:#fff;
          border:1px solid #dbeafe;
          flex:0 0 auto;
        }

        .ag-inline-icon{
          width:18px;
          height:18px;
          display:block;
          flex:0 0 auto;
        }
        .ag-inline-icon-sm{
          width:14px;
          height:14px;
        }
        .agx-title-inline{
          display:flex;
          align-items:flex-start;
          gap:10px;
          min-width:0;
        }
        .agx-title-inline .agx-help-btn{
          margin-top:1px;
          width:28px;
          height:28px;
          box-shadow:none;
        }
        .agx-title-inline .agx-help-btn .ag-inline-icon{
          width:18px;
          height:18px;
        }
        .agx-head-actions{
          margin-left:auto;
        }
        .agx-search-box{
          height:34px;
          min-width:250px;
          max-width:290px;
          gap:8px;
        }
        .agx-search-box .ag-inline-icon{
          width:14px !important;
          height:14px !important;
          color:#94a3b8;
        }
        .agx-search-box input{
          min-width:0;
        }
        .agx-toolbar{
          align-items:center;
        }
        .agx-filters{
          align-items:center;
        }
        .agx-btn{
          min-height:34px;
          border-radius:11px;
          white-space:nowrap;
        }
        .agx-action-row{
          justify-content:flex-start;
        }
        .agx-table tbody td:last-child{
          min-width:170px;
        }
        .agx-modal-info{
          max-width:430px;
          border-radius:26px;
          padding:0;
          overflow:hidden;
        }
        .agx-info-head{
          padding:22px 22px 14px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:14px;
          border-bottom:1px solid #edf2f7;
        }
        .agx-info-title{
          display:flex;
          align-items:center;
          gap:10px;
          font-size:18px;
          font-weight:950;
          color:#1f2b3c;
          margin:0;
        }
        .agx-info-title-icon{
          color:#2f78ff;
          display:grid;
          place-items:center;
        }
        .agx-info-close{
          width:30px;
          height:30px;
          border:0;
          border-radius:999px;
          display:grid;
          place-items:center;
          color:#9aa8bb;
          background:#fff;
          cursor:pointer;
        }
        .agx-info-body{
          padding:20px 22px 22px;
        }
        .agx-info-lead{
          margin:0 0 12px;
          color:#516173;
          font-size:13px;
          font-weight:700;
          line-height:1.55;
        }
        .agx-info-list{
          list-style:none;
          margin:0;
          padding:0;
          display:grid;
          gap:10px;
        }
        .agx-info-list li{
          display:grid;
          grid-template-columns:8px 1fr;
          align-items:start;
          gap:10px;
          color:#445369;
          font-size:13px;
          line-height:1.45;
          font-weight:700;
        }
        .agx-info-dot{
          width:6px;
          height:6px;
          border-radius:999px;
          margin-top:7px;
          background:#3b82f6;
        }
        .agx-info-dot.green{ background:#22c55e; }
        .agx-info-dot.red{ background:#ef4444; }
        .agx-info-dot.gray{ background:#94a3b8; }
        .agx-info-note{
          margin-top:18px;
          display:flex;
          gap:10px;
          align-items:flex-start;
          padding:13px 14px;
          border-radius:14px;
          border:1px solid #cfe0fb;
          background:#eef6ff;
          color:#2f63be;
          font-size:12px;
          font-weight:800;
          line-height:1.45;
        }
        .agx-info-actions{
          margin-top:18px;
        }
        .agx-info-actions .agx-btn{
          width:100%;
          min-height:42px;
          border-radius:13px;
          background:#1e293b;
          box-shadow:none;
        }
        .agx-config-item .ag-inline-icon,
        .agx-config-link .ag-inline-icon,
        .agx-access-card .ag-inline-icon{
          width:18px;
          height:18px;
        }

        .agx-view-toggle{
  height:34px;
  padding:3px;
  display:inline-flex;
  align-items:center;
  gap:3px;
  border:1px solid #dfe6ef;
  border-radius:999px;
  background:#f8fafc;
}

.agx-view-btn{
  width:30px;
  height:28px;
  border:0;
  border-radius:999px;
  display:grid;
  place-items:center;
  background:transparent;
  color:#9aa8b8;
  cursor:pointer;
  transition:.16s ease;
}

.agx-view-btn.is-active{
  background:#ffffff;
  color:#334155;
  box-shadow:0 2px 8px rgba(15,23,42,.08);
}

.agx-view-btn .ag-inline-icon{
  width:14px !important;
  height:14px !important;
}

.agx-force-hidden{
  display:none !important;
}

.agx-cards-mode{
  display:grid !important;
  grid-template-columns:repeat(auto-fit,minmax(260px,1fr));
  gap:14px;
  padding:0 26px 20px;
}

.agx-sort-th{
  border:0;
  background:transparent;
  color:inherit;
  font:inherit;
  text-transform:inherit;
  letter-spacing:inherit;
  display:inline-flex;
  align-items:center;
  gap:7px;
  cursor:pointer;
  padding:0;
}

.agx-sort-th:hover{
  color:#334155;
}

.agx-sort-th.is-active{
  color:#2563eb;
}

.agx-sort-icon{
  display:inline-flex;
  flex-direction:column;
  gap:0;
  font-size:7px;
  line-height:.75;
  color:#b6c2d1;
}

.agx-sort-icon .is-selected{
  color:#2563eb;
}

.agx-btn-results{
  min-height:30px;
  padding:0 11px;
  border-radius:9px;
  background:#f4f7ff;
  border-color:#cddaff;
  color:#274bd6;
  box-shadow:0 4px 10px rgba(76, 104, 255, .08);
}

.agx-btn-results:hover:not(:disabled){
  background:#eef3ff;
  border-color:#b9c8ff;
  color:#1f3eb8;
  transform:translateY(-1px);
}

.agx-info-warning{
  margin-top:12px;
  display:flex;
  gap:10px;
  align-items:flex-start;
  padding:13px 14px;
  border-radius:14px;
  border:1px solid #ffd57a;
  background:#fff9e8;
  color:#b45309;
  font-size:12px;
  font-weight:800;
  line-height:1.45;
}

@media (max-width: 640px){
  .agx-cards-mode{
    padding-left:16px;
    padding-right:16px;
  }
}

        @media (max-width: 720px){
          .agx-config-grid{ grid-template-columns:1fr; }
          .agx-search-box{ min-width:100%; }
        }

       .agx-result-modal{
  width:min(900px, 96vw);
  overflow:hidden;
}

.agx-result-hero{
  border:1px solid var(--agx-border);
  background:linear-gradient(180deg,#ffffff,#f8fbff);
  border-radius:22px;
  padding:18px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  flex-wrap:wrap;
}

.agx-result-hero-left{
  display:flex;
  align-items:center;
  gap:14px;
  min-width:0;
}

.agx-result-badge{
  width:54px;
  height:54px;
  border-radius:18px;
  display:grid;
  place-items:center;
  font-size:24px;
  background:#eef6ff;
  border:1px solid #d5e6ff;
}

.agx-result-badge.ok{
  background:#eaf8ef;
  border-color:#caeed7;
}

.agx-result-badge.bad{
  background:#fff1f1;
  border-color:#f4d0d0;
}

.agx-result-title{
  margin-top:3px;
  color:var(--agx-title);
  font-size:30px;
  font-weight:950;
  letter-spacing:-.04em;
}

.agx-result-percent{
  min-width:96px;
  height:70px;
  border-radius:20px;
  display:grid;
  place-items:center;
  background:#1e293b;
  color:#fff;
  font-size:24px;
  font-weight:950;
  box-shadow:0 12px 24px rgba(15,23,42,.16);
}

.agx-result-percent.ok{
  background:#198754;
}

.agx-result-percent.bad{
  background:#c24141;
}

.agx-result-kpi-grid{
  margin-top:16px;
  display:grid;
  grid-template-columns:repeat(4,minmax(0,1fr));
  gap:12px;
}

.agx-result-kpi{
  border:1px solid var(--agx-border);
  background:#fbfdff;
  border-radius:18px;
  padding:14px;
}

.agx-result-kpi-label{
  font-size:10px;
  color:#8ea0b5;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.06em;
}

.agx-result-kpi-value{
  margin-top:7px;
  color:#17243a;
  font-size:21px;
  font-weight:950;
}

.agx-result-section{
  margin-top:16px;
  border:1px solid var(--agx-border);
  border-radius:20px;
  overflow:hidden;
  background:#fff;
}

.agx-result-section-head{
  padding:14px 16px;
  border-bottom:1px solid #edf2f7;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
}

.agx-result-section-title{
  margin:0;
  color:#1f2b3c;
  font-size:15px;
  font-weight:950;
}

.agx-result-questions{
  display:grid;
}

.agx-result-question{
  padding:15px 16px;
  border-bottom:1px solid #f0f3f8;
}

.agx-result-question:last-child{
  border-bottom:none;
}

.agx-result-question-top{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
}

.agx-result-question-title{
  color:#24364b;
  font-size:13px;
  font-weight:900;
  line-height:1.45;
}

.agx-result-answer-grid{
  margin-top:10px;
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:10px;
}

.agx-result-answer-box{
  border:1px solid #edf2f7;
  background:#f8fafc;
  border-radius:14px;
  padding:11px;
}

.agx-result-answer-label{
  font-size:10px;
  color:#8ea0b5;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.05em;
}

.agx-result-answer-value{
  margin-top:5px;
  color:#334155;
  font-size:12px;
  font-weight:800;
  line-height:1.4;
  word-break:break-word;
}

.agx-result-explanation{
  margin-top:10px;
  padding:11px;
  border-radius:14px;
  background:#eef6ff;
  border:1px solid #d5e6ff;
  color:#365f9e;
  font-size:12px;
  font-weight:750;
  line-height:1.45;
}

@media (max-width: 760px){
  .agx-result-kpi-grid{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }

  .agx-result-answer-grid{
    grid-template-columns:1fr;
  }

  .agx-result-percent{
    width:100%;
  }
} 

.agx-exp-modal{
  width:min(980px,96vw);
  max-height:92vh;
  overflow:hidden;
  border-radius:28px;
  background:#f8fafc;
}

.agx-exp-head{
  height:auto;
  min-height:68px;
  padding:20px 24px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:18px;
  border-bottom:1px solid #e8edf4;
  background:#ffffff;
}

.agx-exp-title{
  margin:0;
  color:#071836;
  font-size:22px;
  font-weight:950;
  letter-spacing:-.04em;
}

.agx-exp-sub{
  margin:4px 0 0;
  color:#8a98aa;
  font-size:12px;
  font-weight:800;
}

.agx-exp-actions{
  display:flex;
  align-items:center;
  gap:10px;
}

.agx-exp-download{
  height:38px;
  padding:0 18px;
  border-radius:13px;
  border:1px solid #d7e0ec;
  background:#ffffff;
  color:#0f1f35;
  font-size:12px;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 5px 14px rgba(15,23,42,.04);
}

.agx-exp-close{
  width:38px;
  height:38px;
  border:0;
  border-radius:999px;
  background:#f1f5f9;
  color:#94a3b8;
  display:grid;
  place-items:center;
  cursor:pointer;
}

.agx-exp-body{
  padding:28px;
  overflow:auto;
  max-height:calc(92vh - 70px);
}

.agx-exp-summary-card{
  border:1px solid #dfe7f1;
  background:#ffffff;
  border-radius:26px 26px 0 0;
  padding:26px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:18px;
}

.agx-exp-person{
  display:flex;
  align-items:center;
  gap:18px;
  min-width:0;
}

.agx-exp-avatar{
  width:74px;
  height:74px;
  border-radius:18px;
  display:grid;
  place-items:center;
  font-size:23px;
  font-weight:950;
  color:#166534;
  background:#dcfce7;
  border:1px solid #bbf7d0;
}

.agx-exp-avatar.bad{
  color:#991b1b;
  background:#fee2e2;
  border-color:#fecaca;
}

.agx-exp-avatar.pending{
  color:#92400e;
  background:#fef3c7;
  border-color:#fde68a;
}

.agx-exp-student-row{
  display:flex;
  align-items:center;
  gap:8px;
}

.agx-exp-student-icon{
  color:#2563eb;
  font-weight:950;
}

.agx-exp-student-name{
  margin:0;
  color:#172033;
  font-size:19px;
  font-weight:950;
}

.agx-exp-meta-line{
  margin-top:8px;
  display:flex;
  gap:12px;
  flex-wrap:wrap;
  color:#64748b;
  font-size:12px;
  font-weight:800;
}

.agx-exp-meta-line span:last-child{
  color:#2563eb;
}

.agx-exp-result-box{
  min-width:230px;
  padding:18px 22px;
  border-radius:20px;
  text-align:center;
  border:1px solid #fed7aa;
  background:#fff7ed;
}

.agx-exp-result-box.ok{
  border-color:#bbf7d0;
  background:#f0fdf4;
}

.agx-exp-result-box.bad{
  border-color:#fecaca;
  background:#fff1f2;
}

.agx-exp-result-box.pending{
  border-color:#fde68a;
  background:#fffbeb;
}

.agx-exp-result-status{
  color:#b91c1c;
  font-size:11px;
  font-weight:950;
  letter-spacing:.16em;
  text-transform:uppercase;
}

.agx-exp-result-box.ok .agx-exp-result-status{
  color:#15803d;
}

.agx-exp-result-box.pending .agx-exp-result-status{
  color:#b45309;
}

.agx-exp-score{
  margin-top:6px;
  color:#172033;
  font-size:28px;
  font-weight:950;
  letter-spacing:-.04em;
}

.agx-exp-score-sub{
  margin-top:8px;
  display:flex;
  justify-content:center;
  gap:14px;
  color:#64748b;
  font-size:11px;
  font-weight:900;
}

.agx-exp-detail-strip{
  border:1px solid #dfe7f1;
  border-top:0;
  background:#ffffff;
  border-radius:0 0 26px 26px;
  padding:22px 26px;
  display:grid;
  grid-template-columns:1fr 220px;
  gap:22px;
  box-shadow:0 10px 20px rgba(15,23,42,.05);
}

.agx-exp-strip-title{
  color:#9aa6b7;
  font-size:12px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.06em;
}

.agx-exp-total-line{
  margin-top:16px;
  display:flex;
  align-items:center;
  flex-wrap:wrap;
  gap:8px;
  color:#334155;
  font-size:12px;
  font-weight:850;
}

.agx-dot{
  width:7px;
  height:7px;
  border-radius:999px;
  display:inline-block;
}

.agx-dot.ok{ background:#10b981; }
.agx-dot.bad{ background:#ef4444; }
.agx-dot.warn{ background:#f97316; }

.agx-exp-bar{
  margin-top:11px;
  width:100%;
  height:10px;
  border-radius:999px;
  overflow:hidden;
  background:#e5e7eb;
  display:flex;
}

.agx-exp-bar span.ok{ background:#10b981; }
.agx-exp-bar span.bad{ background:#ef4444; }
.agx-exp-bar span.warn{ background:#f97316; }

.agx-exp-time-block{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  border-left:1px solid #eef2f7;
}

.agx-exp-time-value{
  margin-top:8px;
  color:#172033;
  font-size:25px;
  font-weight:950;
  letter-spacing:-.04em;
}

.agx-exp-time-sub{
  margin-top:6px;
  color:#2563eb;
  font-size:11px;
  font-weight:900;
}

.agx-exp-grid-three{
  margin-top:22px;
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:20px;
}

.agx-exp-grid-two{
  margin-top:22px;
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:20px;
}

.agx-exp-info-card{
  min-height:128px;
  border:1px solid #dfe7f1;
  background:#ffffff;
  border-radius:24px;
  padding:22px;
  box-shadow:0 8px 18px rgba(15,23,42,.04);
}

.agx-exp-card-title{
  color:#9aa6b7;
  font-size:12px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.07em;
}

.agx-exp-list{
  margin-top:14px;
  display:grid;
  gap:10px;
}

.agx-exp-list div{
  display:flex;
  align-items:center;
  gap:8px;
  color:#1f2937;
  font-size:13px;
  font-weight:850;
}

.agx-exp-list span{
  color:#2563eb;
}

.agx-exp-list small{
  color:#7c8797;
  font-size:10px;
  font-weight:900;
}

.agx-exp-center{
  text-align:center;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
}

.agx-exp-mode{
  margin-top:12px;
  color:#7c3aed;
  font-size:18px;
  font-weight:950;
}

.agx-exp-mode.presencial{
  color:#0f766e;
}

.agx-exp-mini-btn{
  margin-top:16px;
  height:36px;
  padding:0 18px;
  border-radius:999px;
  border:1px solid #dfe7f1;
  background:#ffffff;
  color:#334155;
  font-size:12px;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 6px 14px rgba(15,23,42,.06);
}

.agx-exp-config-lines{
  margin-top:14px;
  display:grid;
  gap:11px;
}

.agx-exp-config-lines div{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  color:#475569;
  font-size:12px;
  font-weight:900;
}

.agx-exp-config-lines strong{
  padding:5px 9px;
  border-radius:8px;
  font-size:11px;
  font-weight:950;
}

.agx-exp-config-lines .is-on{
  color:#15803d;
  background:#dcfce7;
}

.agx-exp-config-lines .is-off{
  color:#64748b;
  background:#f1f5f9;
}

.agx-exp-config-lines .is-warn{
  color:#b45309;
  background:#fef3c7;
}

.agx-exp-teacher{
  margin-top:18px;
  display:flex;
  align-items:center;
  gap:14px;
}

.agx-exp-teacher-icon{
  width:38px;
  height:38px;
  display:grid;
  place-items:center;
  color:#2563eb;
  border-radius:12px;
  background:#eff6ff;
}

.agx-exp-teacher strong{
  display:block;
  color:#172033;
  font-size:14px;
  font-weight:950;
}

.agx-exp-teacher small{
  display:block;
  margin-top:3px;
  color:#2563eb;
  font-size:11px;
  font-weight:800;
}

.agx-exp-review-head{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
}

.agx-exp-review-pill{
  padding:7px 13px;
  border-radius:9px;
  font-size:11px;
  font-weight:950;
  text-transform:uppercase;
}

.agx-exp-review-pill.ok{
  color:#15803d;
  background:#dcfce7;
  border:1px solid #bbf7d0;
}

.agx-exp-review-pill.bad{
  color:#b91c1c;
  background:#fee2e2;
  border:1px solid #fecaca;
}

.agx-exp-review-pill.pending{
  color:#b45309;
  background:#fef3c7;
  border:1px solid #fde68a;
}

.agx-exp-observations{
  margin-top:14px;
  padding:16px;
  border-radius:15px;
  border:1px solid #e8edf4;
  background:#f8fafc;
}

.agx-exp-ob-title{
  color:#7c8797;
  font-size:11px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.06em;
}

.agx-exp-observations p{
  margin:10px 0 0;
  color:#334155;
  font-size:13px;
  font-weight:750;
  line-height:1.55;
}

.agx-exp-big-value{
  margin-top:14px;
  color:#172033;
  font-size:18px;
  font-weight:950;
}

.agx-exp-muted{
  margin-top:6px;
  color:#7c8797;
  font-size:12px;
  font-weight:800;
}

.agx-exp-supervision{
  margin-top:14px;
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}

.agx-exp-supervision span{
  padding:7px 9px;
  border-radius:10px;
  background:#f1f5f9;
  color:#475569;
  font-size:11px;
  font-weight:900;
}

.agx-exp-questions{
  margin-top:22px;
  border:1px solid #dfe7f1;
  background:#ffffff;
  border-radius:24px;
  overflow:hidden;
}

.agx-exp-questions-head{
  padding:18px 22px;
  border-bottom:1px solid #eef2f7;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}

.agx-exp-questions-head h5{
  margin:0;
  color:#172033;
  font-size:16px;
  font-weight:950;
}

.agx-exp-questions-head p{
  margin:4px 0 0;
  color:#8a98aa;
  font-size:12px;
  font-weight:800;
}

.agx-exp-questions-head span{
  padding:7px 11px;
  border-radius:999px;
  background:#eff6ff;
  color:#2563eb;
  font-size:12px;
  font-weight:950;
}

.agx-exp-question-list{
  display:grid;
}

.agx-exp-question{
  padding:18px 22px;
  border-bottom:1px solid #f1f5f9;
}

.agx-exp-question:last-child{
  border-bottom:0;
}

.agx-exp-question-top{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
}

.agx-exp-question-top strong{
  color:#1f2937;
  font-size:13px;
  line-height:1.45;
}

.agx-exp-question-top span{
  padding:6px 10px;
  border-radius:999px;
  font-size:10px;
  font-weight:950;
  text-transform:uppercase;
  flex:0 0 auto;
}

.agx-exp-question-top span.ok{
  color:#15803d;
  background:#dcfce7;
}

.agx-exp-question-top span.bad{
  color:#b91c1c;
  background:#fee2e2;
}

.agx-exp-question-top span.pending{
  color:#64748b;
  background:#f1f5f9;
}

.agx-exp-answer-grid{
  margin-top:13px;
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}

.agx-exp-answer-grid div{
  padding:13px;
  border-radius:14px;
  background:#f8fafc;
  border:1px solid #eef2f7;
}

.agx-exp-answer-grid label{
  display:block;
  color:#94a3b8;
  font-size:10px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.06em;
}

.agx-exp-answer-grid p{
  margin:6px 0 0;
  color:#334155;
  font-size:12px;
  font-weight:800;
  line-height:1.45;
  word-break:break-word;
}

.agx-exp-question-score{
  margin-top:10px;
  color:#64748b;
  font-size:12px;
  font-weight:900;
}

.agx-exp-feedback{
  margin-top:12px;
  padding:12px 14px;
  border-radius:14px;
  color:#365f9e;
  background:#eff6ff;
  border:1px solid #dbeafe;
  font-size:12px;
  font-weight:800;
  line-height:1.45;
}

.agx-exp-empty-detail{
  padding:22px;
  color:#64748b;
  font-size:13px;
  font-weight:850;
}

@media (max-width: 860px){
  .agx-exp-head,
  .agx-exp-summary-card{
    align-items:flex-start;
    flex-direction:column;
  }

  .agx-exp-result-box{
    width:100%;
  }

  .agx-exp-detail-strip,
  .agx-exp-grid-three,
  .agx-exp-grid-two{
    grid-template-columns:1fr;
  }

  .agx-exp-time-block{
    border-left:0;
    border-top:1px solid #eef2f7;
    padding-top:16px;
  }

  .agx-exp-answer-grid{
    grid-template-columns:1fr;
  }

  .agx-exp-body{
    padding:16px;
  }
}

      `}</style>

      <section className="agx-card agx-top-card">
        <div className="agx-top-layout">
          <div className="agx-top-main">
            <div className="agx-top-icon">
              <ExamDocIcon />
            </div>

            <div className="agx-top-copy">
              <p className="agx-kicker">Agendamiento de examen</p>
              <h1 className="agx-title">
                {loadingCourse ? "Cargando examen..." : course?.nombre || "Examen"}
              </h1>
              <div className="agx-subtitle">
                Código: <strong>{course?.codigo_curso || codigoCurso}</strong>
              </div>
            </div>
          </div>

          <div className="agx-side">
            <span className={summaryStatus.cls}>
              <CalendarIcon />
              {summaryStatus.label}
            </span>

            <button
              type="button"
              className="agx-hero-btn agx-hero-btn-secondary"
              disabled
            >
              Descargar certificado
            </button>
          </div>
        </div>

        <div className="agx-alerts">
          {courseErr && (
            <div className="agx-alert agx-alert-error">
              <span>⚠️</span>
              <span>{courseErr}</span>
            </div>
          )}

          {schedulesErr && (
            <div className="agx-alert agx-alert-error">
              <span>⚠️</span>
              <span>{schedulesErr}</span>
            </div>
          )}

          {(toast || cooldownActive) && (
            <div
              className={`agx-alert ${cooldownActive
                ? "agx-alert-info"
                : toast?.kind === "success"
                  ? "agx-alert-success"
                  : "agx-alert-info"
                }`}
            >
              <span>{cooldownActive ? "⏳" : toast?.kind === "success" ? "✅" : "ℹ️"}</span>
              <span>
                {cooldownActive
                  ? `Podrás volver a agendar en ${cooldownSecsLeft}s.`
                  : toast?.msg}
              </span>
            </div>
          )}

          {anyOpenBooking && alreadyBooked && !hasPassedFinal && (
            <div className="agx-alert agx-alert-success">
              <span>✅</span>
              <span>
                Ya es la hora de tu examen. Usa el botón “Verificar acceso” en tu agendamiento para continuar.
              </span>
            </div>
          )}
        </div>
      </section>

      <section className="agx-card agx-main-card">
        <div className="agx-main-head">
          <div className="agx-title-inline">
            <div>
              <h2 className="agx-main-title">Mis agendamientos</h2>
              <p className="agx-main-sub">Historial y estado de tus evaluaciones</p>
            </div>

            <button
              type="button"
              className="agx-help-btn"
              title="Información de mis agendamientos"
              aria-label="Información de mis agendamientos"
              onClick={() => setInfoModal("bookings")}
            >
              <HelpIcon />
            </button>
          </div>

          <div className="agx-head-actions">
            <div className="agx-view-toggle" aria-label="Vista de mis agendamientos">
              <button
                type="button"
                className={`agx-view-btn ${bookingViewMode === "cards" ? "is-active" : ""}`}
                onClick={() => setBookingViewMode("cards")}
                title="Vista en cuadros"
              >
                <GridIcon />
              </button>

              <button
                type="button"
                className={`agx-view-btn ${bookingViewMode === "table" ? "is-active" : ""}`}
                onClick={() => setBookingViewMode("table")}
                title="Vista en lista"
              >
                <ListIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="agx-toolbar">
          <div className="agx-filters">
            <label className="agx-search-box" aria-label="Buscar mis agendamientos">
              <SearchIcon />
              <input
                type="text"
                value={bookingSearch}
                onChange={(e) => setBookingSearch(e.target.value)}
                placeholder="Buscar por fecha..."
              />
            </label>
            <button
              type="button"
              className={`agx-filter ${bookingFilter === "todos" ? "is-active-dark" : ""}`}
              onClick={() => setBookingFilter("todos")}
            >
              Todos
            </button>

            <button
              type="button"
              className={`agx-filter ${bookingFilter === "agendados" ? "is-active-blue" : ""}`}
              onClick={() => setBookingFilter("agendados")}
            >
              Agendados
            </button>
            <button
              type="button"
              className={`agx-filter ${bookingFilter === "evaluados" ? "is-active-green" : ""}`}
              onClick={() => setBookingFilter("evaluados")}
            >
              Evaluados
            </button>
          </div>
        </div>

        {!filteredMyBookings.length ? (
          <div className="agx-empty">
            {bookingSearch.trim()
              ? "No se encontraron agendamientos con ese criterio."
              : bookingFilter === "evaluados"
                ? "No tienes agendamientos evaluados."
                : bookingFilter === "agendados"
                  ? "No tienes agendamientos activos o cancelados."
                  : "Aún no tienes agendamientos registrados."}
          </div>
        ) : (
          <>
            <div className={`desktop-only agx-table-wrap ${bookingViewMode === "cards" ? "agx-force-hidden" : ""}`}>
              <table className="agx-table">
                <thead>
                  <tr>
                    <th>{renderSortHeader("booking", "fecha", "Fecha y hora")}</th>
                    <th>{renderSortHeader("booking", "modalidad", "Modalidad")}</th>
                    <th>{renderSortHeader("booking", "estado", "Estado")}</th>
                    <th>{renderSortHeader("booking", "registro", "Registro")}</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMyBookings.map((b) => {
                    const busyKey = String(
                      b.booking_id ??
                      `hid:${b.horario_examen_id || ""}:${b.fecha_examen || ""}:${b.hora_inicio || ""}`
                    );
                    const isCancelling = !!cancelBusy[busyKey];
                    const openNow = isWindowOpenFor(b, nowTs);
                    const msUntil = msUntilWindowStart(b, nowTs);
                    const willOpenSoon =
                      msUntil != null && msUntil > 0 && msUntil <= 2 * 60 * 60 * 1000;
                    const isFinal = isBookingFinal(b);
                    const approvedBool =
                      typeof b?.aprobado === "boolean" ? b.aprobado : null;

                    const canStartThisBooking =
                      openNow &&
                      !hasPassedFinal &&
                      !isFinal &&
                      !isBookingCancelled(b) &&
                      isStudentEnabledForExam(b) &&
                      (typeof b?.aprobado !== "boolean" || b.aprobado !== true);

                    const showCancel = !isBookingCancelled(b) && !isBookingFinal(b);
                    const cancelDisabled =
                      busy ||
                      isCancelling ||
                      openNow ||
                      !canCancelBooking(b);

                    const st = statusUI(b);

                    return (
                      <tr key={busyKey}>
                        <td>
                          <div className="agx-date">{formatDate(b.fecha_examen)}</div>
                          <div className="agx-time">
                            {formatTime(b.hora_inicio)} - {formatTime(b.hora_fin)}
                          </div>
                        </td>

                        <td>
                          <div className="agx-cell-stack">
                            <span className={b.is_virtual ? "ag-pill ag-pill-purple" : "ag-pill ag-pill-green"}>
                              {b.is_virtual ? "Virtual" : "Presencial"}
                            </span>
                            {!b.is_virtual && (
                              <div className="agx-place">{scheduleLocationLabel(b)}</div>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="agx-cell-stack">
                            <span className={st.pill}>{st.label}</span>

                            {isUnitControlled(b) && !isStudentEnabledForExam(b) && !isBookingCancelled(b) && !isBookingFinal(b) && (
                              <span className="ag-pill ag-pill-amber">Pendiente de habilitación</span>
                            )}

                            {isFinal && approvedBool === true && (
                              <span className="ag-pill ag-pill-green">Aprobado</span>
                            )}

                            {isFinal && approvedBool === false && (
                              <span className="ag-pill ag-pill-red">Reprobado</span>
                            )}

                            {!openNow && willOpenSoon && (
                              <span className="ag-pill ag-pill-amber">
                                Inicia en {msToNice(msUntil)}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="agx-btn agx-btn-ghost"
                            onClick={() => setBookingDetail(b)}
                          >
                            <EyeIcon />
                            Detalles
                          </button>
                        </td>

                        <td>
                          <div className="agx-action-row">
                            {(!isBookingCancelled(b) && !isBookingFinal(b) && isBookingActive(b, nowTs)) && (
                              <button
                                type="button"
                                className="agx-btn agx-btn-enter"
                                onClick={() => handleVerifyAccess(b)}
                              >
                                <ArrowRightIcon />
                                Verificar acceso
                              </button>
                            )}

                            {showCancel && (
                              <button
                                type="button"
                                className="agx-btn agx-btn-danger"
                                onClick={() => handleCancelBooking(b)}
                                disabled={cancelDisabled}
                              >
                                <CloseIcon />
                                {isCancelling ? "Cancelando..." : "Cancelar"}
                              </button>
                            )}

                            {hasExamResult(b) && (
                              <button
                                type="button"
                                className="agx-btn agx-btn-results"
                                onClick={() => setResultDetail(b)}
                              >
                                <BarChartIcon />
                                Ver resultados
                              </button>
                            )}

                            {!canStartThisBooking && !showCancel && !hasExamResult(b) && (
                              <span className="agx-note">Sin acciones</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={`agx-mobile-list ${bookingViewMode === "cards" ? "agx-cards-mode" : "mobile-only"}`}>
              {paginatedMyBookings.map((b) => {
                const busyKey = String(
                  b.booking_id ??
                  `hid:${b.horario_examen_id || ""}:${b.fecha_examen || ""}:${b.hora_inicio || ""}`
                );
                const isCancelling = !!cancelBusy[busyKey];
                const openNow = isWindowOpenFor(b, nowTs);
                const msUntil = msUntilWindowStart(b, nowTs);
                const willOpenSoon =
                  msUntil != null && msUntil > 0 && msUntil <= 2 * 60 * 60 * 1000;
                const isFinal = isBookingFinal(b);
                const approvedBool =
                  typeof b?.aprobado === "boolean" ? b.aprobado : null;
                const canStartThisBooking =
                  openNow &&
                  !hasPassedFinal &&
                  !isFinal &&
                  !isBookingCancelled(b) &&
                  isStudentEnabledForExam(b) &&
                  (typeof b?.aprobado !== "boolean" || b.aprobado !== true);
                const showCancel = !isBookingCancelled(b) && !isBookingFinal(b);
                const cancelDisabled =
                  busy ||
                  isCancelling ||
                  openNow ||
                  !canCancelBooking(b);
                const st = statusUI(b);

                return (
                  <div key={busyKey} className="agx-mobile-card">
                    <div className="agx-mobile-top">
                      <div>
                        <div className="agx-date">{formatDate(b.fecha_examen)}</div>
                        <div className="agx-time">
                          {formatTime(b.hora_inicio)} - {formatTime(b.hora_fin)}
                        </div>
                        {!b.is_virtual && (
                          <div className="agx-place">{scheduleLocationLabel(b)}</div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="agx-btn agx-btn-ghost"
                        onClick={() => setBookingDetail(b)}
                      >
                        <EyeIcon />
                        Detalles
                      </button>
                    </div>

                    <div className="agx-mobile-meta">
                      <span className={b.is_virtual ? "ag-pill ag-pill-purple" : "ag-pill ag-pill-green"}>
                        {b.is_virtual ? "Virtual" : "Presencial"}
                      </span>
                      <span className={st.pill}>{st.label}</span>

                      {isUnitControlled(b) && !isStudentEnabledForExam(b) && !isBookingCancelled(b) && !isBookingFinal(b) && (
                        <span className="ag-pill ag-pill-amber">Pendiente de habilitación</span>
                      )}
                      {isFinal && approvedBool === true && (
                        <span className="ag-pill ag-pill-green">Aprobado</span>
                      )}
                      {isFinal && approvedBool === false && (
                        <span className="ag-pill ag-pill-red">Reprobado</span>
                      )}
                      {!openNow && willOpenSoon && (
                        <span className="ag-pill ag-pill-amber">
                          Inicia en {msToNice(msUntil)}
                        </span>
                      )}
                    </div>

                    <div className="agx-mobile-actions">
                      {(!isBookingCancelled(b) && !isBookingFinal(b) && isBookingActive(b, nowTs)) && (
                        <button
                          type="button"
                          className="agx-btn agx-btn-enter"
                          onClick={() => handleVerifyAccess(b)}
                        >
                          <ArrowRightIcon />
                          Verificar acceso
                        </button>
                      )}

                      {showCancel && (
                        <button
                          type="button"
                          className="agx-btn agx-btn-danger"
                          onClick={() => handleCancelBooking(b)}
                          disabled={cancelDisabled}
                        >
                          <CloseIcon />
                          {isCancelling ? "Cancelando..." : "Cancelar"}
                        </button>
                      )}

                      {hasExamResult(b) && (
                        <button
                          type="button"
                          className="agx-btn agx-btn-results"
                          onClick={() => setResultDetail(b)}
                        >
                          <BarChartIcon />
                          Ver resultados
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {filteredMyBookings.length > 0 && (
          <div className="agx-pager">
            <span>Página {Math.min(myPage, totalPagesMyBookings)} de {totalPagesMyBookings}</span>
            <button type="button" onClick={() => setMyPage((p) => Math.max(1, p - 1))} disabled={myPage <= 1}>‹</button>
            <button type="button" onClick={() => setMyPage((p) => Math.min(totalPagesMyBookings, p + 1))} disabled={myPage >= totalPagesMyBookings}>›</button>
          </div>
        )}

        <div className="agx-separator" />

        <div ref={schedulesSectionRef}>
          <div className="agx-schedules-head">
            <div className="agx-title-inline">
              <div>
                <h3 className="agx-schedules-title">Horarios disponibles</h3>
                <p className="agx-schedules-sub">
                  Selecciona el horario que mejor se ajuste a tu disponibilidad.
                </p>
              </div>

              <button
                type="button"
                className="agx-help-btn"
                title="Información de horarios disponibles"
                aria-label="Información de horarios disponibles"
                onClick={() => setInfoModal("schedules")}
              >
                <HelpIcon />
              </button>
            </div>

            <div className="agx-filters">
              <div className="agx-view-toggle" aria-label="Vista de horarios disponibles">
                <button
                  type="button"
                  className={`agx-view-btn ${scheduleViewMode === "cards" ? "is-active" : ""}`}
                  onClick={() => setScheduleViewMode("cards")}
                  title="Vista en cuadros"
                >
                  <GridIcon />
                </button>

                <button
                  type="button"
                  className={`agx-view-btn ${scheduleViewMode === "table" ? "is-active" : ""}`}
                  onClick={() => setScheduleViewMode("table")}
                  title="Vista en lista"
                >
                  <ListIcon />
                </button>
              </div>
              <label className="agx-search-box" aria-label="Buscar horarios disponibles">
                <SearchIcon />
                <input
                  type="text"
                  value={scheduleSearch}
                  onChange={(e) => setScheduleSearch(e.target.value)}
                  placeholder="Buscar por fecha..."
                />
              </label>
              <button
                type="button"
                className={`agx-filter ${scheduleTypeFilter === "todos" ? "is-active-dark" : ""}`}
                onClick={() => setScheduleTypeFilter("todos")}
              >
                Todos
              </button>
              <button
                type="button"
                className={`agx-filter ${scheduleTypeFilter === "virtual" ? "is-active-blue" : ""}`}
                onClick={() => setScheduleTypeFilter("virtual")}
              >
                Virtual
              </button>
              <button
                type="button"
                className={`agx-filter ${scheduleTypeFilter === "presencial" ? "is-active-green" : ""}`}
                onClick={() => setScheduleTypeFilter("presencial")}
              >
                Presencial
              </button>
            </div>
          </div>

          <div
            className={`agx-panel-empty ${showSchedulesPanel ? "is-closed" : "is-open"}`}
            aria-hidden={showSchedulesPanel}
          >
            <div className="agx-empty">
              Los horarios disponibles se muestran debajo para que puedas revisar cupos, modalidad y requisitos.
            </div>
          </div>

          <div
            className={`agx-panel-shell ${showSchedulesPanel ? "is-open" : "is-closed"}`}
            aria-hidden={!showSchedulesPanel}
          >
            <div className="agx-hidden-panel">
              {hasPassedFinal && (
                <div className="agx-alert agx-alert-success" style={{ marginTop: 14 }}>
                  <span>✅</span>
                  <span>
                    Ya aprobaste este examen. No puedes volver a agendar en este curso.
                  </span>
                </div>
              )}

              {alreadyBooked && !hasPassedFinal && (
                <div className="agx-alert agx-alert-info" style={{ marginTop: 14 }}>
                  <span>ℹ️</span>
                  <span>
                    Ya cuentas con un examen agendado actualmente. Cancélalo para elegir otro.
                  </span>
                </div>
              )}
            </div>

            {loadingSchedules ? (
              <div className="agx-empty">Cargando horarios...</div>
            ) : !visibleSchedules.length ? (
              <div className="agx-empty">No hay horarios activos para este curso.</div>
            ) : (
              <>
                <div
                  className={`desktop-only agx-table-wrap ${scheduleViewMode === "cards" ? "agx-force-hidden" : ""}`}
                  style={{ marginTop: 14 }}
                >
                  <table className="agx-table">
                    <thead>
                      <tr>
                        <th>{renderSortHeader("schedule", "fecha", "Fecha y hora")}</th>
                        <th>{renderSortHeader("schedule", "modalidad", "Modalidad")}</th>
                        <th>{renderSortHeader("schedule", "cupos", "Cupos")}</th>
                        <th>{renderSortHeader("schedule", "ubicacion", "Ubicación")}</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSchedules.map((h) => {
                        const ocupados = Number(h.cupos_ocupados || 0);
                        const cupos = Number(h.cupos_disponibles || 0);
                        const lleno = cupos > 0 && ocupados >= cupos;
                        const inactivo = h.activo === false;

                        const canBook =
                          courseCompleted &&
                          !hasPassedFinal &&
                          !alreadyBooked &&
                          !inactivo &&
                          !lleno &&
                          !busy &&
                          !cooldownActive;

                        const pct = Math.min(
                          100,
                          Math.round((ocupados / Math.max(1, cupos)) * 100)
                        );

                        return (
                          <tr key={h.id}>
                            <td>
                              <div className="agx-date">{formatDate(h.fecha_examen)}</div>
                              <div className="agx-time">
                                {formatTime(h.hora_inicio)} - {formatTime(h.hora_fin)}
                              </div>
                            </td>

                            <td>
                              <div className="agx-cell-stack">
                                <span className={h.is_virtual ? "ag-pill ag-pill-purple" : "ag-pill ag-pill-green"}>
                                  {h.is_virtual ? "Virtual" : "Presencial"}
                                </span>

                                {h.requiere_camara_microfono && (
                                  <span className="ag-pill ag-pill-amber">Cámara / mic</span>
                                )}

                                {inactivo && <span className="ag-pill ag-pill-red">Inactivo</span>}
                                {lleno && <span className="ag-pill ag-pill-gray">Sin cupos</span>}
                              </div>
                            </td>

                            <td>
                              <div className="agx-date">
                                {ocupados} / {cupos}
                              </div>
                              <div className="agx-progress">
                                <span
                                  style={{
                                    width: `${pct}%`,
                                    background: lleno ? "#e98383" : "#7ac892",
                                  }}
                                />
                              </div>
                            </td>

                            <td>
                              <div className="agx-date">{scheduleLocationLabel(h)}</div>
                              <div className="agx-time">
                                {h.is_virtual ? "Acceso remoto" : "Asistencia presencial"}
                              </div>
                            </td>

                            <td>
                              <div className="agx-action-row">
                                <button
                                  type="button"
                                  className="agx-btn agx-btn-ghost"
                                  onClick={() => setScheduleDetail(h)}
                                >
                                  <EyeIcon />
                                  Detalles
                                </button>

                                <button
                                  type="button"
                                  className="agx-btn agx-btn-primary"
                                  onClick={() => handleBook(h)}
                                  disabled={!canBook}
                                  title={
                                    courseCompleted
                                      ? hasPassedFinal
                                        ? "Ya aprobaste este examen"
                                        : alreadyBooked
                                          ? "Ya tienes un agendamiento activo"
                                          : inactivo
                                            ? "Horario inactivo"
                                            : lleno
                                              ? "Sin cupos"
                                              : cooldownActive
                                                ? `Espera ${cooldownSecsLeft}s`
                                                : "Agendar"
                                      : "Completa el curso para agendar"
                                  }
                                >
                                  Agendarme
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div
                  className={`agx-mobile-list ${scheduleViewMode === "cards" ? "agx-cards-mode" : "mobile-only"}`}
                  style={{ marginTop: 14 }}
                >
                  {paginatedSchedules.map((h) => {
                    const ocupados = Number(h.cupos_ocupados || 0);
                    const cupos = Number(h.cupos_disponibles || 0);
                    const lleno = cupos > 0 && ocupados >= cupos;
                    const inactivo = h.activo === false;

                    const canBook =
                      courseCompleted &&
                      !hasPassedFinal &&
                      !alreadyBooked &&
                      !inactivo &&
                      !lleno &&
                      !busy &&
                      !cooldownActive;

                    const pct = Math.min(
                      100,
                      Math.round((ocupados / Math.max(1, cupos)) * 100)
                    );

                    return (
                      <div key={h.id} className="agx-schedule-card">
                        <div className="agx-date">{formatDate(h.fecha_examen)}</div>
                        <div className="agx-time">
                          {formatTime(h.hora_inicio)} - {formatTime(h.hora_fin)}
                        </div>
                        <div className="agx-place">{scheduleLocationLabel(h)}</div>

                        <div className="agx-mobile-meta">
                          <span className={h.is_virtual ? "ag-pill ag-pill-purple" : "ag-pill ag-pill-green"}>
                            {h.is_virtual ? "Virtual" : "Presencial"}
                          </span>

                          {h.requiere_camara_microfono && (
                            <span className="ag-pill ag-pill-amber">Cámara / mic</span>
                          )}

                          {inactivo && <span className="ag-pill ag-pill-red">Inactivo</span>}
                          {lleno && <span className="ag-pill ag-pill-gray">Sin cupos</span>}
                        </div>

                        <div style={{ marginTop: 12 }}>
                          <div className="agx-date">
                            {ocupados} / {cupos} cupos
                          </div>
                          <div className="agx-progress">
                            <span
                              style={{
                                width: `${pct}%`,
                                background: lleno ? "#e98383" : "#7ac892",
                              }}
                            />
                          </div>
                        </div>

                        <div className="agx-mobile-actions">
                          <button
                            type="button"
                            className="agx-btn agx-btn-ghost"
                            onClick={() => setScheduleDetail(h)}
                          >
                            <EyeIcon />
                            Detalles
                          </button>
                          <button
                            type="button"
                            className="agx-btn agx-btn-primary"
                            onClick={() => handleBook(h)}
                            disabled={!canBook}
                          >
                            Agendarme
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {visibleSchedules.length > 0 && (
              <div className="agx-pager">
                <span>Página {Math.min(schedulePage, totalPagesSchedules)} de {totalPagesSchedules}</span>
                <button type="button" onClick={() => setSchedulePage((p) => Math.max(1, p - 1))} disabled={schedulePage <= 1}>‹</button>
                <button type="button" onClick={() => setSchedulePage((p) => Math.min(totalPagesSchedules, p + 1))} disabled={schedulePage >= totalPagesSchedules}>›</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {infoModal === "bookings" && (
        <div className="agx-modal-backdrop" onClick={() => setInfoModal(null)}>
          <div className="agx-modal agx-modal-info" onClick={(e) => e.stopPropagation()}>
            <div className="agx-info-head">
              <h4 className="agx-info-title">
                <span className="agx-info-title-icon"><HelpIcon /></span>
                Mis Agendamientos
              </h4>
              <button type="button" className="agx-info-close" onClick={() => setInfoModal(null)} aria-label="Cerrar">
                <CloseIcon />
              </button>
            </div>
            <div className="agx-info-body">
              <p className="agx-info-lead">Consulta el historial y el estado actual de tus exámenes:</p>
              <ul className="agx-info-list">
                <li><span className="agx-info-dot" /><span><strong>Agendado:</strong> Examen próximo. Puedes realizar verificación o cancelar.</span></li>
                <li><span className="agx-info-dot green" /><span><strong>Aprobado:</strong> Examen superado. El certificado estará disponible cuando corresponda.</span></li>
                <li><span className="agx-info-dot red" /><span><strong>Reprobado:</strong> Examen no superado o sin asistencia.</span></li>
                <li><span className="agx-info-dot gray" /><span><strong>Cancelado:</strong> Anulado voluntariamente por ti.</span></li>
              </ul>
              <div className="agx-info-note">
                <HelpIcon />
                <span>Al cancelar, por seguridad, deberás esperar entre 30 a 60 seg. antes de ver horarios nuevamente.</span>
              </div>
              <div className="agx-info-actions">
                <button type="button" className="agx-btn agx-btn-primary" onClick={() => setInfoModal(null)}>
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {infoModal === "schedules" && (
        <div className="agx-modal-backdrop" onClick={() => setInfoModal(null)}>
          <div className="agx-modal agx-modal-info" onClick={(e) => e.stopPropagation()}>
            <div className="agx-info-head">
              <h4 className="agx-info-title">
                <span className="agx-info-title-icon">
                  <HelpIcon />
                </span>
                Horarios Disponibles
              </h4>

              <button
                type="button"
                className="agx-info-close"
                onClick={() => setInfoModal(null)}
                aria-label="Cerrar"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="agx-info-body">
              <p className="agx-info-lead">
                Lista de fechas habilitadas para rendir tu examen B1.
              </p>

              <div className="agx-detail-item">
                <div className="agx-detail-label">Capacidad de Cupos</div>
                <div className="agx-detail-sub">
                  Son limitados. Si un grupo se llena, el sistema solicitará escoger otro.
                </div>
              </div>

              <div className="agx-detail-item" style={{ marginTop: 10 }}>
                <div className="agx-detail-label">Modalidad</div>
                <div className="agx-detail-sub">
                  Virtual desde tu equipo o presencial en laboratorios UTM.
                </div>
              </div>

              <div className="agx-detail-item" style={{ marginTop: 10 }}>
                <div className="agx-detail-label">Requisitos</div>
                <div className="agx-detail-sub">
                  Fíjate en los íconos. Los exámenes virtuales pueden exigir cámara y micrófono habilitados.
                </div>
              </div>

              <div className="agx-info-warning">
                <span>⚠️</span>
                <span>
                  Presiona <strong>Agendarme</strong> solo cuando estés seguro. Si cancelas un agendamiento,
                  deberás esperar unos segundos por seguridad antes de poder escoger uno nuevo.
                </span>
              </div>

              <div className="agx-info-actions">
                <button
                  type="button"
                  className="agx-btn agx-btn-primary"
                  onClick={() => setInfoModal(null)}
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {accessCheck && (() => {
        const msUntil = msUntilWindowStart(accessCheck, nowTs);
        const enabled = isStudentEnabledForExam(accessCheck);
        const openNow = isWindowOpenFor(accessCheck, nowTs);
        const blockedText = !enabled
          ? "Tu docente todavía no ha habilitado este agendamiento."
          : msUntil != null && msUntil > 0
            ? `El examen aún no inicia. Tiempo restante: ${msToNice(msUntil)}.`
            : "El examen no se encuentra dentro de la ventana permitida.";

        return (
          <div className="agx-modal-backdrop" onClick={() => setAccessCheck(null)}>
            <div className="agx-modal agx-verification-modal" onClick={(e) => e.stopPropagation()}>
              <div className="agx-dark-head">
                <div>
                  <h4 className="agx-modal-title agx-modal-title-light">Verificación de acceso</h4>
                  <p className="agx-modal-sub agx-modal-sub-light">
                    {accessCheck.is_virtual ? "Virtual" : "Presencial"} · {formatDate(accessCheck.fecha_examen)}
                  </p>
                </div>
                <button type="button" className="agx-round-close" onClick={() => setAccessCheck(null)}>
                  <CloseIcon />
                </button>
              </div>

              <div className="agx-modal-body">
                <div className="agx-access-card">
                  <div className="agx-access-icon"><ClockIcon /></div>
                  <div>
                    <div className="agx-detail-label">Estado del acceso</div>
                    <div className="agx-detail-value">{openNow ? "Disponible ahora" : "Aún no disponible"}</div>
                    <div className="agx-detail-sub">
                      {openNow
                        ? enabled
                          ? "Puedes continuar al entorno de cámara, micrófono y código del examen."
                          : "Puedes continuar a la sala de espera; allí verás el estado de habilitación del docente en tiempo real."
                        : blockedText}
                    </div>
                  </div>
                </div>

                <div className="agx-detail-grid" style={{ marginTop: 14 }}>
                  <div className="agx-detail-item"><div className="agx-detail-label">Hora</div><div className="agx-detail-value">{formatTime(accessCheck.hora_inicio)} - {formatTime(accessCheck.hora_fin)}</div></div>
                  <div className="agx-detail-item"><div className="agx-detail-label">Cámara</div><div className="agx-detail-value">{scheduleNeedsCamera(accessCheck) ? "Requerida" : "No requerida"}</div></div>
                  <div className="agx-detail-item"><div className="agx-detail-label">Micrófono</div><div className="agx-detail-value">{scheduleNeedsMic(accessCheck) ? "Requerido" : "No requerido"}</div></div>
                  <div className="agx-detail-item"><div className="agx-detail-label">Ubicación / enlace</div><div className="agx-detail-value">{scheduleLocationLabel(accessCheck)}</div></div>
                </div>

                <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button type="button" className="agx-btn agx-btn-ghost" onClick={() => setAccessCheck(null)}>Cerrar</button>
                  <button
                    type="button"
                    className="agx-btn agx-btn-enter"
                    disabled={!openNow}
                    onClick={() => {
                      setAccessCheck(null);
                      startExamForBooking(accessCheck);
                    }}
                  >
                    <ArrowRightIcon />
                    Continuar al examen
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {bookingDetail && (
        <div className="agx-modal-backdrop" onClick={() => setBookingDetail(null)}>
          <div className="agx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="agx-modal-head">
              <div>
                <h4 className="agx-modal-title">Detalle del agendamiento</h4>
                <p className="agx-modal-sub">Revisión completa del registro seleccionado</p>
              </div>

              <button
                type="button"
                className="agx-btn agx-btn-ghost"
                onClick={() => setBookingDetail(null)}
              >
                Cerrar
              </button>
            </div>

            <div className="agx-modal-body">
              <div className="agx-detail-grid">
                <div className="agx-detail-item">
                  <div className="agx-detail-label">Fecha</div>
                  <div className="agx-detail-value">
                    {formatDate(bookingDetail.fecha_examen)}
                  </div>
                </div>

                <div className="agx-detail-item">
                  <div className="agx-detail-label">Hora</div>
                  <div className="agx-detail-value">
                    {formatTime(bookingDetail.hora_inicio)} - {formatTime(bookingDetail.hora_fin)}
                  </div>
                </div>

                <div className="agx-detail-item">
                  <div className="agx-detail-label">Modalidad</div>
                  <div className="agx-detail-value">
                    {bookingDetail.is_virtual ? "Virtual" : "Presencial"}
                  </div>
                  {!bookingDetail.is_virtual && (
                    <div className="agx-detail-sub">{scheduleLocationLabel(bookingDetail)}</div>
                  )}
                </div>

                <div className="agx-detail-item">
                  <div className="agx-detail-label">Estado</div>
                  <div className="agx-detail-value">{statusUI(bookingDetail).label}</div>
                </div>

                <div className="agx-detail-item">
                  <div className="agx-detail-label">Registro</div>
                  <div className="agx-detail-value">{formatBookedAt(bookingDetail.agendado_en_ts)}</div>
                  <div className="agx-detail-sub">Fecha de creación del agendamiento</div>
                </div>

                <div className="agx-detail-item">
                  <div className="agx-detail-label">Resultado académico</div>
                  <div className="agx-detail-value">{bookingAcademicState(bookingDetail)}</div>
                </div>
              </div>

              <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                {isWindowOpenFor(bookingDetail, nowTs) &&
                  isBookingActive(bookingDetail, nowTs) &&
                  !hasPassedFinal && (
                    <button
                      type="button"
                      className="agx-btn agx-btn-enter"
                      onClick={() => {
                        setBookingDetail(null);
                        startExamForBooking(bookingDetail);
                      }}
                    >
                      <ArrowRightIcon />
                      Ingresar al examen
                    </button>
                  )}

                {!isBookingCancelled(bookingDetail) &&
                  !isBookingFinal(bookingDetail) &&
                  canCancelBooking(bookingDetail) &&
                  !isWindowOpenFor(bookingDetail, nowTs) && (
                    <button
                      type="button"
                      className="agx-btn agx-btn-danger"
                      onClick={() => {
                        setBookingDetail(null);
                        handleCancelBooking(bookingDetail);
                      }}
                    >
                      <CloseIcon />
                      Cancelar agendamiento
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {resultDetail && (() => {
        const r = getResultPayload(resultDetail);

        const studentName =
          [
            resultDetail?.estudiante_nombres ?? user?.nombres,
            resultDetail?.estudiante_apellidos ?? user?.apellidos,
          ]
            .filter(Boolean)
            .join(" ")
            .trim() || "Estudiante";

        const studentEmail =
          resultDetail?.estudiante_email ||
          user?.email ||
          "Correo no registrado";

        const studentCode =
          resultDetail?.estudiante_codigo ||
          user?.codigo_institucional ||
          "—";

        const expedienteId =
          resultDetail?.presentacion_id ||
          resultDetail?.booking_id ||
          resultDetail?.id ||
          "—";

        const approvedLabel =
          r.approved === true ? "APROBADO" : r.approved === false ? "REPROBADO" : "SIN VALIDAR";

        const resultTone =
          r.approved === true ? "ok" : r.approved === false ? "bad" : "pending";

        const pctLabel = formatPercentCompact(r.pct);
        const requiredLabel = formatPercentCompact(r.required ?? 70);

        const realMaxScore =
          r.maxScore ??
          resultDetail?.puntuacion_maxima ??
          resultDetail?.puntos_total_raw ??
          resultDetail?.nota_maxima ??
          20;

        const scoreLabel =
          r.score != null
            ? `${formatScoreCompact(r.score)} / ${formatScoreCompact(realMaxScore)}`
            : `— / ${formatScoreCompact(realMaxScore)}`;

        const totalQuestions =
          Number(resultDetail?.numero_preguntas || 0) ||
          Number(r.autoGradables || 0) ||
          Number(r.detail?.length || 0) ||
          Number(correct + incorrect + unanswered || 0) ||
          0;

        const correct = Number(r.correct || 0);
        const incorrect = Number(r.incorrect || 0);
        const unanswered = Number(r.unanswered || Math.max(0, Number(totalQuestions || 0) - correct - incorrect));

        const totalForBar = Math.max(1, correct + incorrect + unanswered);
        const correctPctBar = Math.max(0, Math.min(100, (correct / totalForBar) * 100));
        const incorrectPctBar = Math.max(0, Math.min(100, (incorrect / totalForBar) * 100));
        const unansweredPctBar = Math.max(0, 100 - correctPctBar - incorrectPctBar);

        const teacherName =
          [
            resultDetail?.docente_nombres,
            resultDetail?.docente_apellidos,
          ]
            .filter(Boolean)
            .join(" ")
            .trim() || "Personal académico";

        const teacherEmail = resultDetail?.docente_email || "Sin correo registrado";

        const proctorLabel = normalizeProctorLabel(r.proctorEstado);
        const proctorApproved = String(r.proctorEstado || "").toLowerCase() === "aprobado";
        const proctorPending = !r.proctorEstado || String(r.proctorEstado).toLowerCase() === "pendiente";

        const motivoLabel = {
          envio: "Enviado por el estudiante",
          timeout: "Finalizado por tiempo agotado",
          docente: "Finalizado por docente",
          abandonado: "Abandonado",
          policy: "Finalizado por política de seguridad",
        }[String(r.motivo || "").toLowerCase()] || "Finalización normal";

        return (
          <div className="agx-modal-backdrop" onClick={() => setResultDetail(null)}>
            <div className="agx-modal agx-exp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="agx-exp-head">
                <div>
                  <h4 className="agx-exp-title">
                    Expediente EXP-{new Date().getFullYear()}-{String(expedienteId).padStart(3, "0")}
                  </h4>
                  <p className="agx-exp-sub">
                    Reporte académico del agendamiento seleccionado
                  </p>
                </div>

                <div className="agx-exp-actions">
                  <button
                    type="button"
                    className="agx-exp-download"
                    onClick={() => window.print()}
                  >
                    ⬇ Descargar reporte
                  </button>

                  <button
                    type="button"
                    className="agx-exp-close"
                    onClick={() => setResultDetail(null)}
                    aria-label="Cerrar"
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>

              <div className="agx-exp-body">
                <section className="agx-exp-summary-card">
                  <div className="agx-exp-person">
                    <div className={`agx-exp-avatar ${resultTone}`}>
                      {initialsFromName(studentName)}
                    </div>

                    <div>
                      <div className="agx-exp-student-row">
                        <span className="agx-exp-student-icon">⊗</span>
                        <h5 className="agx-exp-student-name">{studentName}</h5>
                      </div>

                      <div className="agx-exp-meta-line">
                        <span>CI: {studentCode}</span>
                        <span>{studentEmail}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`agx-exp-result-box ${resultTone}`}>
                    <div className="agx-exp-result-status">{approvedLabel}</div>
                    <div className="agx-exp-score">{scoreLabel}</div>
                    <div className="agx-exp-score-sub">
                      <span>Req: {requiredLabel}</span>
                      <span>Obt: {pctLabel}</span>
                    </div>
                  </div>
                </section>

                <section className="agx-exp-detail-strip">
                  <div className="agx-exp-answer-block">
                    <div className="agx-exp-strip-title">☑ Detalle de respuestas</div>

                    <div className="agx-exp-total-line">
                      <strong>Total: {totalQuestions || "—"}</strong>
                      <span className="agx-dot ok"></span>
                      <span>{correct} Acertadas</span>
                      <span className="agx-dot bad"></span>
                      <span>{incorrect} Falladas</span>
                      <span className="agx-dot warn"></span>
                      <span>{unanswered} Sin responder</span>
                    </div>

                    <div className="agx-exp-bar">
                      <span style={{ width: `${correctPctBar}%` }} className="ok"></span>
                      <span style={{ width: `${incorrectPctBar}%` }} className="bad"></span>
                      <span style={{ width: `${unansweredPctBar}%` }} className="warn"></span>
                    </div>
                  </div>

                  <div className="agx-exp-time-block">
                    <div className="agx-exp-strip-title">Duración estudiante</div>
                    <div className="agx-exp-time-value">
                      {formatDurationClock(r.timeSpent)}
                    </div>
                    <div className="agx-exp-time-sub">
                      ⏱ {resultDetail?.tiempo_limite_minutos || "—"} min. efec.
                    </div>
                  </div>
                </section>

                <section className="agx-exp-grid-three">
                  <div className="agx-exp-info-card">
                    <div className="agx-exp-card-title">Info del curso</div>

                    <div className="agx-exp-list">
                      <div>
                        <span>▣</span>
                        <strong>{resultDetail?.curso_nombre || "Curso"}</strong>
                        <small>({resultDetail?.codigo_curso || codigoCurso})</small>
                      </div>

                      <div>
                        <span>▣</span>
                        <strong>{formatDate(resultDetail.fecha_examen)}</strong>
                      </div>

                      <div>
                        <span>◷</span>
                        <strong>{formatTime(resultDetail.hora_inicio)} - {formatTime(resultDetail.hora_fin)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="agx-exp-info-card agx-exp-center">
                    <div className="agx-exp-card-title">Modalidad</div>

                    <div className={`agx-exp-mode ${resultDetail.is_virtual ? "virtual" : "presencial"}`}>
                      {resultDetail.is_virtual ? "⌁ Virtual" : "⌂ Presencial"}
                    </div>

                    <button
                      type="button"
                      className="agx-exp-mini-btn"
                      onClick={() => setScheduleDetail(resultDetail)}
                    >
                      Ver más detalles
                    </button>
                  </div>

                  <div className="agx-exp-info-card">
                    <div className="agx-exp-card-title">Configuración</div>

                    <div className="agx-exp-config-lines">
                      <div>
                        <span>▣ Cámara:</span>
                        <strong className={scheduleNeedsCamera(resultDetail) ? "is-on" : "is-off"}>
                          {scheduleNeedsCamera(resultDetail) ? "Activa" : "No req."}
                        </strong>
                      </div>

                      <div>
                        <span>♩ Micrófono:</span>
                        <strong className={scheduleNeedsMic(resultDetail) ? "is-on" : "is-off"}>
                          {scheduleNeedsMic(resultDetail) ? "Activo" : "No req."}
                        </strong>
                      </div>

                      <div>
                        <span>⚑ Penalizaciones:</span>
                        <strong className={boolish(resultDetail?.cierre_por_penalizaciones) ? "is-warn" : "is-off"}>
                          {boolish(resultDetail?.cierre_por_penalizaciones)
                            ? `${resultDetail?.limite_penalizaciones || 0} límite`
                            : "Sin cierre"}
                        </strong>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="agx-exp-grid-two">
                  <div className="agx-exp-info-card">
                    <div className="agx-exp-card-title">Personal académico</div>

                    <div className="agx-exp-teacher">
                      <div className="agx-exp-teacher-icon">⊗</div>
                      <div>
                        <strong>{teacherName}</strong>
                        <small>{teacherEmail}</small>
                      </div>
                    </div>
                  </div>

                  <div className="agx-exp-info-card">
                    <div className="agx-exp-review-head">
                      <div className="agx-exp-card-title">Revisión del docente</div>
                      <span className={`agx-exp-review-pill ${proctorApproved ? "ok" : proctorPending ? "pending" : "bad"}`}>
                        {proctorLabel}
                      </span>
                    </div>

                    <div className="agx-exp-observations">
                      <div className="agx-exp-ob-title">▧ Observaciones emitidas</div>
                      <p>
                        {r.proctorMotivo ||
                          resultDetail?.proctor_motivo ||
                          (proctorPending
                            ? "La revisión de seguridad aún se encuentra pendiente."
                            : "Identidad verificada correctamente. Sin novedades disciplinarias.")}
                      </p>
                    </div>
                  </div>
                </section>

                <section className="agx-exp-grid-three">
                  <div className="agx-exp-info-card">
                    <div className="agx-exp-card-title">Presentación</div>
                    <div className="agx-exp-big-value">#{r.presentationId ?? "—"}</div>
                    <div className="agx-exp-muted">Intento {r.attemptNumber ?? "—"}</div>
                  </div>

                  <div className="agx-exp-info-card">
                    <div className="agx-exp-card-title">Finalización</div>
                    <div className="agx-exp-big-value">{formatBookedAt(r.finishedAt)}</div>
                    <div className="agx-exp-muted">{motivoLabel}</div>
                  </div>

                  <div className="agx-exp-info-card">
                    <div className="agx-exp-card-title">Supervisión</div>
                    <div className="agx-exp-supervision">
                      <span>Fotos: {resultDetail?.fotos_tomadas ?? 0}</span>
                      <span>Alertas: {resultDetail?.alertas_seguridad ?? 0}</span>
                      <span>Novedades: {resultDetail?.novedades_docente ?? 0}</span>
                    </div>
                  </div>
                </section>

                <section className="agx-exp-questions">
                  <div className="agx-exp-questions-head">
                    <div>
                      <h5>Detalle por pregunta</h5>
                      <p>Respuestas registradas durante la presentación.</p>
                    </div>

                    <span>{r.detail.length} registro{r.detail.length === 1 ? "" : "s"}</span>
                  </div>

                  {r.detail.length > 0 ? (
                    <div className="agx-exp-question-list">
                      {r.detail.map((item, idx) => {
                        const ok =
                          item?.correcto === true ||
                          item?.es_correcto === true ||
                          item?.is_correct === true;

                        const bad =
                          item?.correcto === false ||
                          item?.es_correcto === false ||
                          item?.is_correct === false;

                        const questionText =
                          item?.pregunta ||
                          item?.enunciado ||
                          item?.question ||
                          `Pregunta ${idx + 1}`;

                        const studentAnswer =
                          item?.respuesta_estudiante ??
                          item?.respuesta ??
                          item?.answer ??
                          item?.valor_respuesta ??
                          item?.student_answer;

                        const correctAnswer =
                          item?.respuesta_correcta ??
                          item?.correcta ??
                          item?.correct_answer;

                        const points =
                          item?.puntos_obtenidos ??
                          item?.puntosGanados ??
                          item?.puntos ??
                          item?.score;

                        const maxPoints =
                          item?.puntos_maximos ??
                          item?.puntosTotal ??
                          item?.puntos_posibles ??
                          item?.maxScore;

                        return (
                          <article key={idx} className="agx-exp-question">
                            <div className="agx-exp-question-top">
                              <strong>{idx + 1}. {questionText}</strong>
                              <span className={ok ? "ok" : bad ? "bad" : "pending"}>
                                {ok ? "Correcta" : bad ? "Incorrecta" : "Revisar"}
                              </span>
                            </div>

                            <div className="agx-exp-answer-grid">
                              <div>
                                <label>Tu respuesta</label>
                                <p>{answerToText(studentAnswer)}</p>
                              </div>

                              <div>
                                <label>Respuesta correcta</label>
                                <p>{answerToText(correctAnswer)}</p>
                              </div>
                            </div>

                            {(points != null || maxPoints != null) && (
                              <div className="agx-exp-question-score">
                                Puntaje: {points ?? "—"} / {maxPoints ?? "—"}
                              </div>
                            )}

                            {(item?.explicacion || item?.feedback || item?.retroalimentacion) && (
                              <div className="agx-exp-feedback">
                                {item?.explicacion || item?.feedback || item?.retroalimentacion}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="agx-exp-empty-detail">
                      No hay desglose por pregunta para esta presentación, pero el resultado general fue cargado correctamente.
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        );
      })()}

      {scheduleDetail && (
        <div className="agx-modal-backdrop" onClick={() => setScheduleDetail(null)}>
          <div className="agx-modal agx-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="agx-dark-head">
              <div>
                <h4 className="agx-modal-title agx-modal-title-light">Configuración del Examen</h4>
                <p className="agx-modal-sub agx-modal-sub-light">
                  {scheduleDetail.is_virtual ? "Virtual" : "Presencial"} · {formatDate(scheduleDetail.fecha_examen)}
                </p>
              </div>
              <button type="button" className="agx-round-close" onClick={() => setScheduleDetail(null)}>
                <CloseIcon />
              </button>
            </div>

            <div className="agx-modal-body">
              <div className="agx-config-grid">
                <div className="agx-config-item">
                  <div className="agx-config-icon">🏆</div>
                  <div><div className="agx-detail-label">Nota máxima</div><div className="agx-detail-value">{scheduleDetail.nota_maxima || 20} pts</div></div>
                </div>
                <div className="agx-config-item">
                  <div className="agx-config-icon"><ClockIcon /></div>
                  <div><div className="agx-detail-label">Duración</div><div className="agx-detail-value">{scheduleDetail.tiempo_limite_minutos || "—"} minutos</div></div>
                </div>
                <div className="agx-config-item">
                  <div className="agx-config-icon agx-ok"><CameraIcon /></div>
                  <div><div className="agx-detail-label">Cámara</div><div className="agx-detail-value">{scheduleNeedsCamera(scheduleDetail) ? "Requerida" : "No requerida"}</div></div>
                </div>
                <div className="agx-config-item">
                  <div className="agx-config-icon agx-ok"><MicIcon /></div>
                  <div><div className="agx-detail-label">Micrófono</div><div className="agx-detail-value">{scheduleNeedsMic(scheduleDetail) ? "Requerido" : "No requerido"}</div></div>
                </div>
              </div>

              <div className="agx-config-link">
                <LinkIcon />
                <div>
                  <div className="agx-detail-label">{scheduleDetail.is_virtual ? "Enlace del examen" : "Ubicación del examen"}</div>
                  <div className="agx-detail-value">{scheduleLocationLabel(scheduleDetail)}</div>
                </div>
              </div>

              <div className="agx-detail-grid" style={{ marginTop: 14 }}>
                <div className="agx-detail-item"><div className="agx-detail-label">Horario</div><div className="agx-detail-value">{formatTime(scheduleDetail.hora_inicio)} - {formatTime(scheduleDetail.hora_fin)}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Preguntas</div><div className="agx-detail-value">{scheduleDetail.numero_preguntas || "—"}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Cupos</div><div className="agx-detail-value">{Number(scheduleDetail.cupos_ocupados || 0)} / {Number(scheduleDetail.cupos_disponibles || 0)}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Código de acceso</div><div className="agx-detail-value">{boolish(scheduleDetail.requiere_codigo) ? "Requerido" : "No requerido"}</div><div className="agx-detail-sub">{scheduleDetail.codigo_horario ? `Horario ${scheduleDetail.codigo_horario}` : "El código se solicitará al ingresar si está configurado."}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Aprobación mínima</div><div className="agx-detail-value">{scheduleDetail.porcentaje_aprobacion ?? 70}%</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Habilitación</div><div className="agx-detail-value">{boolish(scheduleDetail.habilitacion_unitaria_estudiantes) ? "Manual por docente" : "Automática"}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Fotos requeridas</div><div className="agx-detail-value">{scheduleDetail.numero_fotos_requeridas ?? 0}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Micrófono</div><div className="agx-detail-value">{scheduleDetail.umbral_decibeles_microfono != null ? `${scheduleDetail.umbral_decibeles_microfono} dB` : "Sin umbral"}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Penalizaciones</div><div className="agx-detail-value">{boolish(scheduleDetail.cierre_por_penalizaciones) ? `Cierre al llegar a ${scheduleDetail.limite_penalizaciones || 0}` : "Sin cierre automático"}</div></div>
                <div className="agx-detail-item"><div className="agx-detail-label">Resumen final</div><div className="agx-detail-value">{boolish(scheduleDetail.mostrar_resumen_final) ? "Visible" : "Oculto"}</div></div>
              </div>

              <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                <button type="button" className="agx-btn agx-btn-ghost" onClick={() => setScheduleDetail(null)}>Cerrar</button>
                <button
                  type="button"
                  className="agx-btn agx-btn-primary"
                  onClick={() => {
                    const copy = scheduleDetail;
                    setScheduleDetail(null);
                    handleBook(copy);
                  }}
                  disabled={!courseCompleted || hasPassedFinal || alreadyBooked || busy || cooldownActive}
                >
                  Agendar este horario
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {termsOpen && (
        <div className="agx-modal-backdrop" onClick={closeTermsModal}>
          <div className="agx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="agx-modal-head">
              <div>
                <h4 className="agx-modal-title">Términos y condiciones del examen</h4>
                <p className="agx-modal-sub">
                  {termsData?.version
                    ? `Versión ${termsData.version}`
                    : "Revisión obligatoria antes de agendar"}
                </p>
              </div>

              <button
                type="button"
                className="agx-btn agx-btn-ghost"
                onClick={closeTermsModal}
              >
                Cerrar
              </button>
            </div>

            <div className="agx-modal-body">
              <div className="agx-terms-box">
                {termsData?.contenido || "No hay términos configurados actualmente."}
              </div>

              <label className="agx-check">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>Acepto los términos y condiciones del examen.</span>
              </label>

              <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="agx-btn agx-btn-ghost"
                  onClick={closeTermsModal}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="agx-btn agx-btn-primary"
                  disabled={!termsAccepted || busy || cooldownActive}
                  onClick={confirmTermsAndBook}
                >
                  Acepto y agendar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
